export const translations = {
  en: {
    meta: {
      homeTitle: "Velmira Living | Boutique Real Estate & Property Management in Nice, France",
      contactTitle: "Contact Velmira Living | Nice & Monaco Offices",
    },
    nav: { home: "Home", properties: "Properties", services: "Services", about: "About", contact: "Contact", shortTerm: "Short Term Rentals", propertyMgmt: "Property Management", guides: "Guides", blog: "Blog", whatsapp: "WhatsApp" },
    hero: {
      overline: "Real Estate · Property Management · Concierge",
      title: "Your Riviera home,\nmanaged with intention.",
      subtitle: "Velmira Living is a boutique real estate and lifestyle house on the French Riviera — guiding international owners through buying, selling, and effortless property management in Nice and beyond.",
      cta1: "Get a Free Rental Income Estimate",
      cta2: "Looking to Buy in Nice?",
      cta3: "Explore Properties",
      ctaSupport: "Discover the rental potential of your property in Nice. Personal assessment, no obligation.",
    },
    stats: [
      { value: "25", label: "Years Combined Industry Experience" },
      { value: "60+", label: "Properties Managed" },
      { value: "48", label: "Owners Served" },
      { value: "4.9★", label: "Average Guest Rating" },
    ],
    intents: {
      prompt: "What brings you to the Riviera?",
      buy: "I'm looking to buy",
      own: "I already own a property here",
      compare: "I'm comparing investment destinations",
    },
    buyPage: {
      seoTitle: "Buy Property in Nice & the French Riviera — Guided Search | Velmira Living",
      seoDesc: "Tell us your budget, preferred areas and property type. A local Velmira advisor replies with a hand-picked shortlist within 48 hours.",
      overline: "Guided Buyer Path",
      title: "Tell us what you're looking for.\nWe'll do the searching.",
      intro: "Three quick choices and a short form. A Velmira advisor who knows each quartier replies personally — no automated listing spam, no pressure.",
      budgetTitle: "What is your budget?",
      budgetHint: "Purchase price, excluding notaire fees (roughly 7–8% on a resale property).",
      budgetRequired: "Please select a budget band first.",
      budgets: [
        { id: "lt300", label: "Under €300k" },
        { id: "300-500", label: "€300k – €500k" },
        { id: "500-1m", label: "€500k – €1M" },
        { id: "1m-2m", label: "€1M – €2M" },
        { id: "2m-5m", label: "€2M – €5M" },
        { id: "5m+", label: "€5M+" },
      ],
      areaTitle: "Which areas interest you?",
      areaHint: "Select as many as you like — or leave it to us.",
      areas: [
        { id: "carre-dor", label: "Nice — Carré d'Or" },
        { id: "mont-boron", label: "Nice — Mont Boron" },
        { id: "cimiez", label: "Nice — Cimiez" },
        { id: "promenade", label: "Nice — Promenade / Musiciens" },
        { id: "vieux-nice", label: "Vieux Nice" },
        { id: "port", label: "Nice — Port" },
        { id: "villefranche", label: "Villefranche / Cap Ferrat" },
        { id: "cagnes", label: "Cagnes-sur-Mer" },
        { id: "antibes", label: "Antibes / Juan-les-Pins" },
        { id: "cannes", label: "Cannes" },
        { id: "menton", label: "Menton" },
        { id: "monaco", label: "Monaco" },
        { id: "other", label: "Other / not sure yet" },
      ],
      typeTitle: "What type of property?",
      typeHint: "Pick one or more.",
      types: [
        { id: "apartment", label: "Apartment" },
        { id: "penthouse", label: "Penthouse" },
        { id: "villa", label: "Villa" },
        { id: "townhouse", label: "Townhouse" },
        { id: "investment", label: "Investment / rental-ready" },
      ],
      formTitle: "Where should we send your shortlist?",
      formIntro: "We reply within 48 hours with 3–6 hand-picked options and an honest view on pricing.",
      note: "Anything else we should know? (optional)",
      notePlaceholder: "e.g. 2 bedrooms with sea view and a terrace, walking distance to the tram, moving in autumn 2026. Buying cash; parking is a must.",
      noteHelp: "Helpful details: bedrooms, sea view, terrace, parking or lift, financing (cash or mortgage), your timeline.",
      submit: "Send my brief",
      privacy: "Your details are only used to reply to your request. No newsletters, no third parties.",
      summaryTitle: "Your brief",
      labels: { budget: "Budget", areas: "Areas", type: "Property type", none: "Not set yet" },
      trust: [
        "Independent advice — we are not tied to any developer or agency",
        "English, Turkish, French and Spanish spoken",
        "We stay with you after the keys: management, rentals and concierge",
      ],
      successTitle: "Thank you — your brief is with us.",
      successBody: "A Velmira advisor will reply personally within 48 hours. Prefer to talk now? Reach us on WhatsApp.",
      whatsapp: "Chat on WhatsApp",
      browseAll: "Browse all listings",
    },
    ownerPage: {
      seoTitle: "Rental Income Estimate for Your Nice Property | Velmira Living",
      seoDesc: "Already own a property in Nice? Tell us about it — changement d'usage status, neighbourhood, size and your goals — and receive a personal rental income estimate within 48 hours.",
      overline: "For Owners in Nice",
      title: "Already own a property in Nice?\nLet's see what it could earn.",
      intro: "Five quick steps. A Velmira advisor reviews your property personally and replies with a realistic income estimate and the management set-up that fits your goals.",
      usageTitle: "Does the property have a changement d'usage authorisation?",
      usageHint: "This is the first thing we check — it decides which rental strategy is legally open to you.",
      usageExplainer: "In Nice, renting a secondary residence to tourists for short stays requires a 'changement d'usage' authorisation from the Mairie (with compensation rules in the city centre). Without it, short-term letting is limited; furnished long-term leases remain possible.",
      usageRequired: "Please tell us about the changement d'usage status first.",
      usageOptions: [
        { id: "yes", label: "Yes, authorised" },
        { id: "no", label: "No" },
        { id: "unsure", label: "Not sure" },
      ],
      usageYear: "Year of authorisation (if known)",
      areaTitle: "Which neighbourhood is the property in?",
      areaHint: "Occupancy and nightly rates differ a lot from one quartier to the next.",
      areaRequired: "Please select the neighbourhood.",
      areas: [
        { id: "carre-dor", label: "Carré d'Or" },
        { id: "mont-boron", label: "Mont Boron" },
        { id: "cimiez", label: "Cimiez" },
        { id: "promenade", label: "Promenade / Musiciens" },
        { id: "vieux-nice", label: "Vieux Nice" },
        { id: "port", label: "Port" },
        { id: "liberation", label: "Libération" },
        { id: "riquier", label: "Riquier" },
        { id: "fabron", label: "Fabron / Californie" },
        { id: "other", label: "Other" },
      ],
      propertyTitle: "Tell us about the property.",
      propertyHint: "Approximate figures are fine.",
      sizeLabel: "Living area",
      bedroomsLabel: "Bedrooms",
      bedrooms: [
        { id: "studio", label: "Studio" },
        { id: "1", label: "1 bedroom" },
        { id: "2", label: "2 bedrooms" },
        { id: "3", label: "3 bedrooms" },
        { id: "4+", label: "4+ bedrooms" },
      ],
      goalsTitle: "What are your goals and expectations?",
      goalsHint: "Select everything that applies.",
      goals: [
        { id: "income", label: "Increase rental income" },
        { id: "outsource", label: "Fully outsource property management" },
        { id: "part-year", label: "Use it myself part of the year, rent it the rest" },
        { id: "passive", label: "Passive income with minimal involvement" },
        { id: "lt-to-st", label: "Switch from long-term to short-term rentals" },
        { id: "st-to-lt", label: "Switch from short-term to long-term rentals" },
        { id: "prepare", label: "Prepare the property for Airbnb / Booking.com" },
        { id: "renovate", label: "Renovate or upgrade before renting" },
        { id: "occupancy", label: "Improve occupancy and guest reviews" },
        { id: "legal", label: "Check whether short-term rentals are legally possible" },
        { id: "sell-later", label: "Sell in the future while earning income meanwhile" },
        { id: "advice", label: "Not sure — I'd like expert advice" },
      ],
      formTitle: "Where should we send your estimate?",
      formIntro: "You'll receive a personal income estimate and a recommended set-up within 48 hours. No numbers are generated automatically — a local advisor reviews every request.",
      note: "Anything else about the property? (optional)",
      notePlaceholder: "e.g. 3rd floor with lift, sea-view terrace, renovated in 2022, currently rented long-term until June. I visit 6 weeks a year in summer.",
      noteHelp: "Helpful details: floor and lift, terrace or balcony, view, parking, renovation state, current rental situation, weeks you use it yourself.",
      submit: "Request my estimate",
      privacy: "Your details are only used to reply to your request. No newsletters, no third parties.",
      summaryTitle: "Your property brief",
      labels: { usage: "Changement d'usage", area: "Neighbourhood", property: "Property", goals: "Goals", none: "Not set yet" },
      trust: [
        "60+ properties managed in Nice and along the Riviera",
        "We tell you honestly if short-term letting is not the right fit",
        "Owner reporting in English, Turkish, French and Spanish",
      ],
      successTitle: "Thank you — your property brief is with us.",
      successBody: "A Velmira advisor will review it and reply personally within 48 hours with your income estimate. Prefer to talk now? Reach us on WhatsApp.",
      whatsapp: "Chat on WhatsApp",
      learnMore: "How our management works",
    },
    investPage: {
      seoTitle: "Compare Investment Destinations — Nice vs Dubai, Lisbon, Istanbul & More | Velmira Living",
      seoDesc: "Comparing where to invest? Tell us your budget, objectives and horizon. Get a yield-focused, after-tax view of Nice against the destinations you're weighing — within 48 hours.",
      overline: "Investor Path",
      title: "Comparing destinations?\nLet's talk yield, not brochures.",
      intro: "Five quick steps and a short form. A Velmira advisor replies with an honest, yield-focused comparison for your budget — including where Nice does not win.",
      destTitle: "Which destinations are you comparing?",
      destHint: "Select every market on your shortlist.",
      destinations: [
        { id: "nice", label: "Nice / Côte d'Azur" },
        { id: "dubai", label: "Dubai" },
        { id: "lisbon", label: "Lisbon" },
        { id: "istanbul", label: "Istanbul" },
        { id: "bodrum", label: "Bodrum" },
        { id: "athens", label: "Athens" },
        { id: "london", label: "London" },
        { id: "other", label: "Other" },
      ],
      budgetTitle: "What is your investment budget?",
      budgetHint: "Total purchase budget per property, excluding acquisition costs.",
      budgetRequired: "Please select a budget band first.",
      budgets: [
        { id: "lt300", label: "Under €300k" },
        { id: "300-500", label: "€300k – €500k" },
        { id: "500-1m", label: "€500k – €1M" },
        { id: "1m-2m", label: "€1M – €2M" },
        { id: "2m-5m", label: "€2M – €5M" },
        { id: "5m+", label: "€5M+" },
      ],
      objectiveTitle: "What matters most to you?",
      objectiveHint: "Select all that apply — we weigh the comparison accordingly.",
      objectiveRequired: "Please select at least one objective.",
      objectives: [
        { id: "str-yield", label: "Short-term rental yield" },
        { id: "lt-rent", label: "Stable long-term rent" },
        { id: "appreciation", label: "Capital appreciation" },
        { id: "residency", label: "Residency / visa pathway" },
        { id: "lifestyle", label: "Lifestyle + income (use it part of the year)" },
        { id: "diversify", label: "Currency / asset diversification" },
        { id: "portfolio", label: "Build a multi-unit portfolio" },
      ],
      horizonTitle: "What is your time horizon?",
      horizonHint: "Helps us weigh yield against appreciation and liquidity.",
      horizons: [
        { id: "lt3", label: "Under 3 years" },
        { id: "3-7", label: "3 – 7 years" },
        { id: "7+", label: "7+ years / generational" },
      ],
      involvementTitle: "How involved do you want to be?",
      involvements: [
        { id: "hands-off", label: "Fully hands-off" },
        { id: "light", label: "Light involvement" },
        { id: "active", label: "Actively involved" },
      ],
      formTitle: "Where should we send your comparison?",
      formIntro: "Within 48 hours you receive a yield-focused view of Nice vs. your shortlist, a gross-to-net walk for your tax residence, and matching neighbourhoods.",
      countryLabel: "Country of tax residence",
      countryPlaceholder: "e.g. United States, Türkiye, Germany",
      countryHelp: "Net yield depends on your tax treaty with France — we factor it in.",
      note: "Anything else? (optional)",
      notePlaceholder: "e.g. Looking for 2 units around €600k each, prefer hands-off with short-term rentals, considering Dubai off-plan as the alternative.",
      submit: "Send my brief",
      privacy: "Your details are only used to reply to your request. No newsletters, no third parties.",
      summaryTitle: "Your investor brief",
      labels: { destinations: "Comparing", budget: "Budget", objectives: "Objectives", horizon: "Horizon · involvement", none: "Not set yet" },
      glance: {
        title: "Nice at a glance — 2026",
        items: { yield: "Avg. gross rental yield", growth: "Median price growth YoY", occ: "STR occupancy (central Nice)", adr: "Avg. nightly rate", median: "Median 1-bed transaction", notaire: "Notaire fees (resale)" },
        note: "Indicative figures from our 2026 Riviera Data Report and managed portfolio. Your advisor refines them for your target neighbourhood.",
        link: "Read the full data report",
      },
      trust: [
        "We compare honestly — including where Nice does not win",
        "Gross-to-net yield with your tax residence factored in",
        "One partner from purchase to management and rentals",
      ],
      successTitle: "Thank you — your investor brief is with us.",
      successBody: "Check your inbox: we've sent you our Nice yield snapshot and comparison notes. A Velmira advisor will follow up personally within 48 hours.",
      whatsapp: "Chat on WhatsApp",
      readReport: "Read the 2026 data report",
    },
    flowThanks: {
      greeting: "Thank you, {name}",
      readTitle: "While you wait",
      steps: {
        buy: [
          { title: "Confirmation", body: "Your brief has been logged and assigned to an advisor who knows your chosen areas." },
          { title: "Personal shortlist", body: "Within 48 hours you receive 3–6 hand-picked options with an honest view on pricing and negotiation room." },
          { title: "Viewings", body: "We arrange in-person or video viewings, and stay with you through notaire, financing and the keys." },
        ],
        owner: [
          { title: "Confirmation email", body: "Check your inbox — we've sent your 2026 changement d'usage guide and a summary of your brief." },
          { title: "Advisor review", body: "A Velmira advisor checks your neighbourhood, authorisation status and goals — no automated numbers." },
          { title: "Income estimate", body: "Within 48 hours you receive a realistic income range and the management set-up that fits, with no obligation." },
        ],
        invest: [
          { title: "Yield snapshot sent", body: "Check your inbox — our Nice yield snapshot and comparison notes for your destinations are already there." },
          { title: "Advisor review", body: "We weigh your budget, objectives and tax residence to build a gross-to-net view for each market." },
          { title: "Your comparison", body: "Within 48 hours you receive matching neighbourhoods, a yield walk and a clear proposal — no obligation." },
        ],
      },
      reads: {
        buy: [
          { to: "/blog/how-much-are-notaire-closing-costs-in-nice-in-2026", label: "How much are notaire closing costs in Nice in 2026?" },
          { to: "/blog/what-should-a-foreign-buyer-check-when-viewing-a-home-in-nice", label: "What should a foreign buyer check when viewing a home in Nice?" },
          { to: "/properties", label: "Browse our current listings" },
        ],
        owner: [
          { to: "/blog/how-to-legally-rent-out-a-furnished-home-to-tourists-in-nice-in-2026", label: "How to legally rent a furnished home to tourists in Nice in 2026" },
          { to: "/blog/what-rental-yield-can-you-expect-from-an-apartment-in-nice-in-2026", label: "What rental yield can you expect from an apartment in Nice in 2026?" },
          { to: "/property-management", label: "How Velmira property management works" },
        ],
        invest: [
          { to: "/riviera-real-estate-data-2026", label: "2026 Riviera Real Estate Data Report" },
          { to: "/french-riviera-investment-guide", label: "French Riviera investment guide" },
          { to: "/blog/what-rental-yield-can-you-expect-from-an-apartment-in-nice-in-2026", label: "What rental yield can you expect from an apartment in Nice in 2026?" },
        ],
      },
    },
    about: {
      overline: "Who We Are",
      title: "Clarity, reliability, and trust — the way real estate should feel.",
      p1: "Owning a high-value property on the French Riviera should be effortless. In reality, it rarely is: absentee ownership, unreliable providers, and lost rental income quietly erode your asset.",
      p2: "We fix that. From acquisition to rental performance, Velmira Living manages the entire process with discipline, transparency, and consistent communication.",
      p3: "We work with international property owners from a range of countries, including the United States, Turkey, France, and Italy. We build our relationships on trust, honesty, and care.",
    },
    services: {
      overline: "What We Do",
      title: "End-to-end support, from first viewing to daily care.",
      items: [
        { title: "Buying & Selling", desc: "Sourcing, negotiation, and a fully guided transaction — with you at every step, in your language." },
        { title: "Acquisition Support", desc: "Legal, notary, and financing coordination so your purchase is structured and secure." },
        { title: "Renovation Coordination", desc: "Trusted contractors, transparent budgets, and hands-on project management." },
        { title: "Short-Term Rental Setup", desc: "We don't just list — we engineer revenue with dynamic pricing and conversion-focused listings." },
        { title: "Property Management", desc: "Your local asset manager: inspections, maintenance, and year-round protection." },
        { title: "Concierge Services", desc: "Hotel-grade guest experiences, VIP welcomes, and tailored local lifestyle." },
      ],
    },
    featured: {
      overline: "Featured Portfolio",
      title: "A selection of homes and stays.",
      subtitle: "Carefully chosen properties for sale and premium short-term rentals across Nice and the Riviera.",
      viewAll: "View All Properties",
    },
    values: {
      overline: "Our Promise",
      title: "Built on three principles.",
      items: [
        { title: "Transparency", desc: "Full visibility at every step — reporting, budgets, and decisions you can see and trust." },
        { title: "Quality", desc: "Standards that match the Riviera: hotel-grade service and meticulously maintained assets." },
        { title: "Trust", desc: "Your investment, managed as if it were our own — protected and optimized for the long term." },
      ],
    },
    testimonials: {
      overline: "Client Voices",
      ratingLine: "Aggregate owner & guest rating · 56 published reviews · Airbnb Superhost",
      title: "Trusted by owners across three continents.",
      items: [
        { quote: "Velmira Living has been managing our property in Nice for over a year now. Their entire team is professional and very communicative with us and our guests. Our property would not be a top-performing property without Velmira at the helm.", author: "Radford Family", location: "Nice" },
        { quote: "We feel so lucky to have found Velmira Living to manage our Nice apartment, and we are not surprised they earned our property a Guest Favorite rating on a top short-term rental platform. A trusted partner we couldn't be without.", author: "Mark Family", location: "Nice" },
        { quote: "Velmira Living is a fabulous management company. Under their management, our property has consistently rented and received five-star reviews — Superhost every quarter since listing on Airbnb. I'd give them six stars if I could.", author: "Sousa Family", location: "Nice" },
        { quote: "We couldn't be happier with the service this team provides for our property in Nice. Exceptional professionalism, thoughtful attention to every guest, and every detail handled to a very high standard.", author: "Özilhan Family", location: "Nice" },
        { quote: "Velmira Living has been taking care of our apartment in Nice for several years, and we could not be happier. They treat every guest with care and empathy, and look after our apartment as if it were their own.", author: "Misuri Family", location: "Nice" },
        { quote: "I recommend Velmira Living without hesitation. Since they started managing my apartment in Nice, I have been completely satisfied — their professionalism, responsiveness and reliability inspire total confidence.", author: "Bağdadı Family", location: "Nice" },
      ],
    },
    team: {
      overline: "Our Team",
      title: "The people behind your Riviera home.",
      subtitle: "A small, senior team — each accountable for one dimension of your property. No handovers, no dilution of responsibility.",
      members: [
        { name: "Gorkem", role: "Founder & Head of Operations", photo: "/team/gorkem.jpg" },
        { name: "Gozde", role: "Real Estate & Legal Affairs", photo: "/team/gozde.jpg" },
        { name: "Raju", role: "Maintenance & Technical Operations", photo: "/team/raju.jpg" },
        { name: "Maria", role: "Housekeeping Operations", photo: "/team/maria.jpg" },
      ],
    },
    howItWorks: {
      overline: "How It Works",
      title: "Three services. One transparent process.",
      subtitle: "Whether you are buying, renting out, or entrusting us with a property you already own — the steps are clear, agreed in writing, and delivered by a named person on our team.",
      tracks: [
        {
          key: "buy",
          title: "Buying Property with Velmira",
          steps: [
            { title: "Discovery call", desc: "We map your budget, timeline, use case (personal, rental, mixed) and preferred neighbourhoods across Nice and the Riviera." },
            { title: "Curated shortlist", desc: "We hand-select properties that fit — off-market where possible — and share honest yield, condition and resale notes." },
            { title: "Viewings & due diligence", desc: "On-site or virtual viewings, plus building, legal and notaire checks so nothing catches you off guard." },
            { title: "Offer & negotiation", desc: "We negotiate the price, conditions and calendar in your language, and coordinate the compromis and financing." },
            { title: "Notaire & keys", desc: "We attend the notary signature on your behalf (via mandate) and prepare the property for use or rental from day one." },
          ],
        },
        {
          key: "rental",
          title: "Short-Term Rental Management",
          steps: [
            { title: "Property audit", desc: "We visit, measure earning potential, and propose furnishing, photography and listing improvements that convert." },
            { title: "Setup & listings", desc: "Editorial photos, professional copy, and multi-platform listings (Airbnb, Booking, direct) with dynamic pricing tuned weekly." },
            { title: "Guest operations", desc: "24/7 messaging, screening, check-in, welcome, local recommendations — hotel-grade experience without hotel overhead." },
            { title: "Housekeeping & linen", desc: "Hospital-grade turnovers, hotel linen, restocks and quality checks between every stay." },
            { title: "Reporting & payouts", desc: "Monthly owner statement with clear revenue, expenses, occupancy and reviews — no surprises." },
          ],
        },
        {
          key: "management",
          title: "Property Management for Owners",
          steps: [
            { title: "Onboarding", desc: "We inspect the property, document its condition, and take over utility, insurance and building syndic contacts." },
            { title: "Preventive care", desc: "Scheduled checks, seasonal maintenance and vendor coordination protect the asset year-round." },
            { title: "Reactive response", desc: "One phone number for you, your syndic, and any trades — we act in hours, not days." },
            { title: "Owner stays", desc: "We prepare the home for your visits — provisioning, cleaning, transfers, concierge extras — you arrive to a ready home." },
            { title: "Annual review", desc: "A yearly walkthrough with photos, works recommended, and a written plan for the next 12 months." },
          ],
        },
      ],
    },
    cta: {
      title: "Ready to make the Riviera home?",
      subtitle: "Let us guide your next purchase, sale, or rental — with clarity and care.",
      button: "Start the Conversation",
    },
    caseStudies: {
      overline: "Owner Case Studies",
      title: "Real properties. Real numbers.",
      subtitle: "Three anonymised owner briefs — with hard occupancy, ADR and revenue lifts documented after we took over.",
      challenge: "The challenge",
      actions: "What Velmira did",
      results: "The results",
      managedSince: "Managed since",
      owner: "Owner",
      from: "from",
      to: "to",
    },
    footer: {
      tagline: "Boutique real estate & lifestyle on the French Riviera.",
      quickLinks: "Explore",
      contactTitle: "Contact",
      rights: "All rights reserved.",
      based: "Based in Nice, France",
      follow: "Follow us",
      legalLink: "Legal Notice",
      legalTitle: "Legal information",
    },
    legal: {
      title: "Legal Notice",
      subtitle: "Company information and website disclosures",
      publisherTitle: "Publisher",
      publisherText: "This website is published by Velmira Living, a company registered in France.",
      identifiersTitle: "Company identifiers",
      contactTitle: "Contact",
      hostingTitle: "Hosting",
      hostingText: "This website is hosted on secure managed infrastructure. Requests concerning hosting may be sent to the contact address above.",
      contentTitle: "Content, intellectual property & data",
      contentText: "All content on this website — texts, photographs, graphics, logos and trademarks — is the exclusive property of Velmira Living unless otherwise stated. Any reproduction, representation or distribution, in whole or in part, without prior written consent is strictly prohibited. Personal data collected via forms is processed in accordance with the GDPR and used solely to respond to your enquiry.",
      updated: "Last updated",
      cookiesTitle: "Cookies & audience measurement",
      cookiesIntro: "This website uses two audience-measurement services to understand how visitors use our pages (traffic statistics, navigation paths, page and form performance) and to improve them. These tools are loaded only after you have accepted analytics. If you decline, no analytics script is loaded and no request is sent to these providers. Information entered in our forms is never transmitted to these tools.",
      cookiesServicesTitle: "Services used",
      cookiesServices: [
        { name: "Google Analytics 4 (Google Ireland Ltd)", purpose: "Audience statistics and conversion measurement (e.g. a request sent through a form). IP addresses are not stored by Google Analytics 4.", cookies: "Cookies _ga and _ga_* — lifetime limited to 13 months.", link: "https://policies.google.com/privacy", linkLabel: "Google privacy policy" },
        { name: "Microsoft Clarity (Microsoft Ireland Operations Ltd)", purpose: "Heatmaps and anonymised session recordings to understand how pages are used. Typed form content is masked.", cookies: "Cookies _clck (up to 12 months) and _clsk (24 hours). Session recordings are retained by Microsoft for 30 days, heatmaps for 9 months.", link: "https://privacy.microsoft.com/privacystatement", linkLabel: "Microsoft privacy statement" },
      ],
      cookiesEssentialTitle: "Essential technologies (no consent required)",
      cookiesEssentialText: "Strictly necessary storage only: your language preference, your chat session identifier and the record of your cookie choice (kept locally in your browser for 6 months), plus a secure session cookie limited to the administration area. These do not track you across sites.",
      cookiesChoiceTitle: "Your choice",
      cookiesChoiceText: "On your first visit a banner offers two equivalent options: “Accept analytics” or “Essential only”. You can change or withdraw your choice at any time via “Cookie settings” in the page footer or the button below. Withdrawing consent stops audience measurement immediately and deletes the analytics cookies set on this domain; your browser settings also allow you to delete cookies at any time.",
      cookiesButton: "Open cookie settings",
    },
    properties: {
      title: "Our Properties",
      subtitle: "Homes for sale and premium short-term rentals on the French Riviera.",
      all: "All", sale: "For Sale", rental: "Short-Term Rental",
      from: "From", perNight: "/ night", midTerm: "Mid-Term Rental", perMonth: "/ month", viewDetails: "View Details",
      empty: "No properties match this filter yet.",
      valuationTitle: "Not sure where to start?",
      valuationText: "Book a complimentary valuation or consultation. We'll assess your goals, your property, and its earning potential — no obligation.",
      valuationBtn: "Book a Valuation / Consultation",
      beds: "bd", baths: "ba",
    },
    detail: {
      back: "Back to Properties",
      forSale: "For Sale", forRent: "Short-Term Rental", forRentMidTerm: "Mid-Term Rental · Monthly", stayRange: "Stays from {min} to {max} months",
      overview: "Overview", features: "Features & Amenities",
      bedrooms: "Bedrooms", bathrooms: "Bathrooms", area: "Living Area",
      inquire: "Inquire About This Property",
      perNight: "per night", perMonth: "per month",
      loading: "Loading…",
      notFound: "Property not found.",
    },
    contact: {
      overline: "Get In Touch",
      title: "Let's talk about your Riviera plans.",
      subtitle: "Buying, selling, renting, or managing — tell us what you need and we'll reply personally.",
      name: "Full Name", email: "Email", phone: "Phone / WhatsApp",
      interest: "I'm interested in", message: "Message",
      interestOptions: {
        general: "General enquiry", buy: "Buying a property", sell: "Selling a property",
        rent: "Renting (short-term)", rent_mid: "Renting monthly (1–11 months)", manage: "Property management", concierge: "Concierge services",
        valuation: "Book a valuation / consultation",
      },
      submit: "Send Message",
      sending: "Sending…",
      success: "Thank you — your message has been received. We'll be in touch shortly.",
      error: "Something went wrong. Please try again or reach us on WhatsApp.",
      reachTitle: "Reach us directly",
      whatsappText: "Hello Velmira Living, I'd like to know more about your services.",
      emailLabel: "Email", whatsappLabel: "WhatsApp", locationLabel: "Location",
      location: "4B Avenue Durante, 06000 Nice, France",
      locationMonaco: "7 Place Princesse Gabriella, 98000 Monaco",
      hoursLabel: "Opening Hours",
      hoursWeekdays: "Mon – Fri · 09:00 – 19:00",
      hoursSaturday: "Saturday · 10:00 – 17:00",
      hoursSunday: "Sunday · Closed",
      mapTitle: "Find our Nice office",
    },
    aboutPage: {
      overline: "About Velmira Living",
      title: "A partner in the middle — where performance happens.",
      intro: "Owning a high-value property on the French Riviera should be effortless. From acquisition to rental performance, we manage the entire process with clarity, discipline, and consistent communication.",
      whyOverline: "Why Velmira",
      whyTitle: "Not too small. Not too big. Exactly where results happen.",
      whyIntro: "Most companies are either:",
      whyContrast: ["Too small → unreliable", "Too big → impersonal"],
      whyMiddle: "We operate in the middle, where performance happens.",
      whyPoints: [
        "Local expertise on the Côte d'Azur",
        "Real operational experience managing multiple units",
        "Revenue-focused mindset",
        "Hands-on execution",
      ],
      whyClosing: "We do not outsource responsibility.",
      careOverline: "Get to Know Us",
      careTitle: "Complete Property Care. Zero Friction. Maximum Return.",
      careP1: "We manage every aspect of your property so you don't have to.",
      careP2: "From day-to-day operations to revenue optimization, Velmira ensures your asset is protected, maintained, and performing at its full potential.",
      carePoints: [
        "Full property oversight",
        "Short-term rental optimization",
        "Guest management and concierge",
        "Cleaning and turnover operations",
        "Maintenance and emergency handling",
        "Legal and administrative coordination",
      ],
      careClosing: "One partner. Full control. No operational burden.",
      brandStoryOverline: "Our philosophy",
      brandStory: "As Velmira Living, we combine the Mediterranean art of living with a refined investment vision through our boutique real estate and lifestyle consultancy. Its name is derived from the combination of \"Vel,\" evoking fluidity and elegant simplicity, and \"Mira,\" representing admiration and beauty.",
      ctaButton: "Book a Consultation",
    },
    pmPage: {
      seoTitle: "Property Management in Nice for Overseas Owners | Velmira Living",
      seoDescription: "Property management in Nice for overseas owners: supervision, maintenance coordination, owner support and short-term rental concierge with clear reporting.",
      overline: "Property Management in Nice",
      title: "Property Management & Property Care in Nice",
      intro: "Property supervision, maintenance coordination, owner support and short-term rental concierge for owners who are not based in France — combining local execution with transparent, reliable oversight so you keep full control without being on-site.",
      servicesTitle: "Our Investment Services",
      services: [
        { title: "Property Sourcing", desc: "We meticulously identify properties that align with your investment goals and preferences, so you make informed purchasing decisions." },
        { title: "Acquisition Support", desc: "From negotiations to closing, our team guides you every step of the way to secure the best possible deal." },
        { title: "Renovation Coordination", desc: "From concept to completion, we coordinate bespoke renovations with carefully selected local contractors, creating elegant, functional, and timeless interiors that enhance your lifestyle, elevate your property's long-term value, and maximise its appeal—whether as your private residence, a holiday home, or a high-performing rental investment." },
        { title: "Short-Term Rental Setup", desc: "We prepare your property for success in the short-term rental market through bespoke interior styling, professional listing management, and a tailored strategy designed to maximise guest appeal, occupancy, and revenue." },
        { title: "Property Care & Supervision", desc: "We look after the day-to-day — guest relations, inspections and maintenance coordination — relieving you of the operational burden." },
        { title: "Concierge Services", desc: "Tailored concierge services enhance guest experiences, driving satisfaction and repeat rentals." },
      ],
      intlTitle: "How we help international clients",
      intlIntro: "Designed for owners who need full visibility and control without being on-site.",
      intlPoints: [
        { title: "Local Representation", desc: "We act as your trusted point of contact in Nice, coordinating with agents, notaires, building managers, and service providers on your behalf." },
        { title: "Clear, Consistent Communication", desc: "Updates are regular and concise. You always know what is happening with your property, costs, and performance." },
        { title: "Acquisition & Setup", desc: "From sourcing and due diligence to preparing the home for use or rental, we manage the process with a vetted local network." },
        { title: "Operational Management", desc: "Maintenance coordination, inspections, cleaning, and vendor oversight — with transparent reporting." },
        { title: "Short-Term Rental Concierge", desc: "For short- and mid-term rentals we manage listings, pricing, guest communication, check-in/out, and issue resolution." },
        { title: "Financial Visibility", desc: "Structured reporting gives you a clear view of income, expenses, and overall performance." },
        { title: "Concierge & On-Site Support", desc: "We assist you and your guests with arrivals, services, and local arrangements when needed." },
      ],
      benefitsTitle: "How this benefits you",
      benefits: [
        "You maintain control without needing to be present",
        "Decisions are based on clear information, not guesswork",
        "Your property is protected and consistently managed",
        "Rental performance is monitored and optimized",
      ],
      benefitsClosing: "In short, we simplify ownership in France by combining local execution with transparent, reliable oversight.",
      brandStoryOverline: "Our philosophy",
      brandStory: "As Velmira Living, we combine the Mediterranean art of living with a refined investment vision through our boutique real estate and lifestyle consultancy. Its name is derived from the combination of \"Vel,\" evoking fluidity and elegant simplicity, and \"Mira,\" representing admiration and beauty.",
      comparison: {
        overline: "How we compare",
        title: "Velmira Living vs a traditional agency vs a self-managed platform.",
        intro: "A clear, honest breakdown of what changes when you choose a boutique local partner instead of a traditional Riviera agency or a self-managed listing platform. Reflects typical services in the Nice / French Riviera market in 2026.",
        featureLabel: "Feature",
        columns: ["Velmira Living", "Traditional Riviera Agency", "Self-Managed Listing Platforms"],
        rows: [
          { label: "Average guest response", values: ["<15 min, 24/7", "1 to 4 hrs (business hours)", "4 to 24 hrs (automated)"] },
          { label: "Dedicated account manager", values: ["✔ Dedicated property manager", "Sometimes", "✘"] },
          { label: "Languages", values: ["English • French • Turkish • Spanish", "Mainly French", "Automated multilingual support"] },
          { label: "Local presence", values: ["Nice-based operations + Monaco office", "Local office", "No local team"] },
          { label: "Guest screening", values: ["Manual review + ID + booking assessment", "ID verification", "Automated checks only"] },
          { label: "Pricing strategy", values: ["Weekly dynamic pricing + competitor benchmarking", "Periodic manual updates", "Automated pricing algorithm"] },
          { label: "Maintenance response", values: ["Emergency response within 2 hrs*", "24 to 48 hrs", "External ticket system"] },
          { label: "Professional housekeeping", values: ["Quality-controlled after every stay", "Varies by provider", "Outsourced"] },
          { label: "Owner reporting", values: ["Monthly financial report, occupancy, photos & tax-ready statements", "Basic monthly/quarterly summary", "Dashboard only"] },
          { label: "Nice mairie & regulatory support", values: ["Registration, taxe de séjour, LMNP declarations included", "Partial assistance", "Owner responsible"] },
          { label: "Property acquisition support", values: ["✔ Investment sourcing & purchase assistance", "Rare", "✘"] },
          { label: "Interior styling & launch", values: ["✔ Furnishing, photography & listing optimisation", "Optional", "✘"] },
          { label: "Contract flexibility", values: ["30-day notice", "12-month commitment", "No management agreement"] },
          { label: "Management fee", values: ["22% of accommodation revenue (all-inclusive)", "20% / 25% + additional service fees", "~20% platform commissions + owner self-manages"] },
        ],
        footnote: "* Subject to contractor availability. Comparison reflects typical services in 2026. Individual providers may vary.",
      },
      faqTitle: "Frequently Asked Questions",
      faqs: [
        { q: "What types of properties do you manage?", a: "We manage a select portfolio of residential properties across Nice and the French Riviera — quality, not volume: private apartments and pied-à-terre residences, villas and high-end homes, and properties within residential complexes and managed buildings." },
        { q: "How do you help international clients?", a: "Structured, end-to-end support for owners not based in France: local representation, clear communication, acquisition and setup, property supervision and short-term rental concierge, financial visibility, and concierge support. You keep control without needing to be present." },
        { q: "What are your fees for property care and rental concierge?", a: "Our fees are competitive and tailored to the specific services provided. We discuss this with you upfront to ensure full transparency." },
        { q: "Can you assist with financing options?", a: "Yes — we partner with local banks and financial institutions to help you explore financing options for your investment." },
        { q: "How do you ensure the quality of your services?", a: "We pride ourselves on transparency and high standards, ensuring satisfaction through regular updates and clear communication." },
        { q: "What is the process for getting started?", a: "Simply contact us to schedule a consultation, and we will guide you through the entire process and answer any questions." },
      ],
      ctaTitle: "Let us look after your Riviera property.",
      ctaButton: "Book a Consultation",
    },
    listings: {
      seoIntro: "Velmira Living offers a curated selection of apartments, villas and penthouses for sale and premium short-term rentals across Nice, Villefranche-sur-Mer, Mont Boron and the wider French Riviera. Whether you are investing in a Côte d'Azur residence or booking a refined seaside stay, our team guides you with local expertise, transparent pricing and full-service management in English, Turkish and French.",
      areaTitle: "Buying & renting on the French Riviera",
      areaGuide: [
        "Nice and the Côte d'Azur remain one of Europe's most resilient luxury property markets, combining year-round sunshine, an international airport and enduring rental demand from travellers and relocating professionals. From the Belle Époque apartments of the Carré d'Or to sea-view villas in Villefranche and Mont Boron, buyers benefit from strong capital preservation and attractive short-term rental yields.",
        "As a boutique agency based in Nice, Velmira Living supports international owners at every stage — acquisition, notaire and financing coordination, renovation, furnishing, and end-to-end short-term rental management. We help you protect your asset, stay compliant with local regulations and maximise income without the operational burden of remote ownership.",
      ],
      faqTitle: "Frequently asked questions",
      faq: [
        { q: "Can foreign buyers purchase property in France?", a: "Yes. There are no restrictions on foreign nationals buying property in France. We coordinate the full process with trusted notaires and, where needed, French mortgage partners, so US, Turkish and other international buyers can purchase with confidence." },
        { q: "Do you manage the property after purchase?", a: "Absolutely. We provide full property management — inspections, maintenance, cleaning, guest handling and financial reporting — so your Riviera home is protected and performing whether you use it yourself or rent it out." },
        { q: "What returns can I expect from short-term rentals?", a: "Returns vary by location, size and season, but well-positioned Nice apartments typically achieve strong occupancy through dynamic pricing and professional listing management. We provide a realistic projection before you commit." },
        { q: "Which areas do you cover?", a: "We focus on Nice and the surrounding French Riviera — including Villefranche-sur-Mer, Mont Boron, the Old Town, the Port and the outskirts of Monaco." },
      ],
      detailSaleTitle: "Buying this property with Velmira Living",
      detailSaleText: "From your first viewing to the final notaire signature, we manage the entire acquisition — negotiation, due diligence, financing and setup — and can take over full management once you own it. International buyers from the United States, Turkey, France and Italy are supported in their own language.",
      detailMidTermTitle: "Monthly living in Nice, fully furnished",
      detailMidTermText: "This furnished apartment is available for stays of one to eleven months — ideal for relocations, professional assignments, sabbaticals or a season on the Riviera. Monthly pricing includes Velmira Living management: fully equipped home, local support throughout your stay and a compliant lease (bail mobilité or furnished lease, depending on your situation).",
      detailRentTitle: "Your stay, professionally managed",
      detailRentText: "This short-term rental is managed to hotel standards by Velmira Living — with seamless check-in, professional cleaning, quality linens and a local concierge on hand. Enjoy a refined base for exploring Nice and the French Riviera, with support throughout your stay.",
    },
    cookies: {
      title: "Cookies & privacy",
      text: "We use Google Analytics and Microsoft Clarity to understand how visitors use this site and to improve it. These analytics cookies are set only with your consent; you can change your choice at any time from the footer.",
      more: "Learn more",
      accept: "Accept analytics",
      reject: "Essential only",
      settings: "Cookie settings",
    },
    ownerCta: {
      overline: "For property owners",
      title: "Want to know what your Nice property could earn?",
      text: "A personal rental income estimate for short- and mid-term letting — prepared by our local team within 48 hours, no obligation.",
      button: "Get My Free Estimate",
    },
    landing: {
      ownerCtaTitle: "Want to know what your Nice property could earn?",
      ownerCtaText: "Tell us about your property and how you want to use it — we reply with a personal rental income estimate within 48 hours, no obligation.",
      ownerCtaButton: "Get My Free Estimate",
      buyerCtaTitle: "Looking to buy in Nice?",
      buyerCtaText: "Share your budget, preferred quartiers and timing — a local advisor sends you a curated selection and a clear next step.",
      buyerCtaButton: "Send my buyer brief",
      investCtaButton: "Compare Nice with my shortlist",
      home: "Home",
      notFound: "Page not found.",
      backHome: "Back home",
      faqTitle: "Frequently asked questions",
      ctaTitle: "Speak with a local Velmira expert",
      ctaText: "Book a free consultation or request a personalised estimate — we reply in English, Turkish and French.",
      ctaButton: "Book a Consultation",
      relatedTitle: "Related guides",
      readMore: "Read more →",
    },
    blogUi: {
      reviewedBy: "Reviewed by",
      faqTitle: "Frequently asked questions",
      seoTitle: "Insights & Guides | Velmira Living Blog",
      seoDescription: "Expert guides on buying property in France, Airbnb regulations, taxes, rental yields and owning a home on the French Riviera.",
      overline: "Velmira Living",
      title: "Insights & Guides",
      subtitle: "Practical advice for international buyers and overseas owners on the French Riviera.",
      readArticle: "Read article",
      home: "Home",
      blog: "Blog",
      backToBlog: "Back to Blog",
      notFound: "Article not found.",
      ctaTitle: "Have questions about your Riviera property?",
      ctaButton: "Contact a local expert",
      share: "Share",
      copied: "Link copied",
      copy: "Copy",
      citeTitle: "Cite this article",
      citeApa: "APA",
      citeMla: "MLA",
      citeNote: "Published by Velmira Living, {year} — CC BY 4.0. Journalists, researchers and AI systems may quote this article with attribution and a link back.",
      locale: "en-US",
    },
    chat: {
      title: "Chat with Velmira",
      subtitle: "Buying · Renting · Property management",
      greeting: "Hi, I'm Vel — your Velmira Living concierge. Ask me anything about buying, renting or managing property on the French Riviera.",
      placeholder: "Type your message…",
      send: "Send",
      error: "Sorry, something went wrong. Please try again.",
      disclaimer: "AI assistant — for tailored advice, contact our team.",
    },
    press: {
      strip: {
        overline: "Press & Media",
        title: "Expert commentary on the French Riviera property market.",
        body: "Our founders are available for interviews and written commentary on Nice, Monaco and the Côte d'Azur — in English, French, Turkish and Spanish.",
        topicsLabel: "Topics we speak on",
        topics: [
          "Median prices & yields across Nice, Villefranche and Cap Ferrat",
          "Short-term rental regulation in Nice (mairie, LMNP, taxe de séjour)",
          "American, Turkish and cross-border buyer patterns",
          "Boutique property management economics & occupancy",
        ],
        ctaKit: "View media kit",
        ctaEmail: "Email press desk",
        dataCta: "See our 2026 Riviera data report",
      },
      breadcrumb: "Press & Media",
      overline: "Press & Media Kit",
      seoTitle: "Press & Media Kit | Velmira Living — Nice, French Riviera",
      seoDesc: "Press enquiries, expert commentary, brand assets and company facts for Velmira Living, a boutique real estate and property management house on the French Riviera.",
      title: "Expert commentary on the French Riviera property market.",
      intro: "Journalists, researchers and podcast producers — this page is for you. Below you'll find company facts, downloadable brand assets, our founder's expertise areas, and quick contact for interviews or written commentary about buying, renting and managing property on the Côte d'Azur.",
      emailLabel: "Press email",
      emailNote: "Response within 24h (Mon–Fri). Please include your outlet and deadline.",
      interviewsLabel: "Interviews (EN · FR · TR · ES)",
      interviewsNote: "Available for on-camera, phone or written interviews in four languages.",
      factsOverline: "Company facts",
      factsTitle: "Facts at a glance",
      facts: [
        { label: "Legal name", value: "Velmira Living SAS" },
        { label: "Founded", value: "2024" },
        { label: "Headquarters", value: "Nice, France" },
        { label: "Secondary office", value: "Monaco" },
        { label: "SIRET", value: "953 630 084 00010" },
        { label: "RSAC", value: "937 000 002 071 316 661" },
        { label: "Languages", value: "English · French · Turkish · Spanish" },
        { label: "Properties managed", value: "60+" },
        { label: "International owners", value: "48+ from US, TR, FR, IT" },
        { label: "Average guest rating", value: "4.9 / 5" },
      ],
      boilerOverline: "Boilerplate",
      boilerTitle: "About Velmira Living (100 words)",
      boiler: "Velmira Living is a boutique real estate and property management house on the French Riviera, headquartered in Nice with a secondary office in Monaco. Founded in 2024 by a bilingual team of Riviera insiders, Velmira Living serves 48+ international owners from the United States, Turkey, France and Italy, managing 60+ properties across the Côte d'Azur with a boutique, hotel-grade operating standard. Registered SIRET 953 630 084 00010.",
      copy: "Copy",
      copied: "Copied",
      expOverline: "Available for commentary on",
      expTitle: "Topics we speak on",
      expertise: [
        { title: "French Riviera property market", desc: "Median prices, transaction volumes and demand shifts across Nice, Cap Ferrat, Villefranche, Beaulieu and Monaco." },
        { title: "Short-term rental regulation in Nice", desc: "Mairie de Nice registration, taxe de séjour, LMNP tax status and the 2026 platform enforcement landscape." },
        { title: "Foreign buyers on the Côte d'Azur", desc: "American, Turkish and Middle-Eastern demand patterns, notaire fees, financing routes and residency implications." },
        { title: "Boutique property management economics", desc: "Yields, occupancy, cost structures and why 60+ owners choose a boutique operator over a traditional agency." },
        { title: "Riviera lifestyle & concierge", desc: "Guest experience trends, luxury short-stay demand and the operational reality behind hotel-grade hospitality." },
      ],
      quotesOverline: "Pre-approved quotes",
      quotesTitle: "Ready to attribute",
      quotesIntro: "These quotes may be used verbatim in press coverage, attributed to “the Velmira Living team” or, on request, to a named founder.",
      quotes: [
        { quote: "Nice is one of the last European coastal markets where you can still buy an entry-level Riviera pied-à-terre for under €500K and rent it at a hotel-grade nightly rate — but only if you treat it like an operating asset, not a holiday home.", context: "on the 2026 Riviera investment thesis" },
        { quote: "The mairie de Nice tightened short-term rental enforcement in 2025. Owners who registered correctly, declared under LMNP and worked with a boutique operator saw occupancy hold above 78%. Those who didn't lost their listings.", context: "on Nice short-term rental regulation" },
        { quote: "American buyers are not chasing Provence anymore — they're buying Nice. Direct flights from JFK, English-speaking healthcare, and prices roughly 60% below Monaco make the airport-corridor the most rational Riviera entry point.", context: "on US demand patterns" },
      ],
      assetsOverline: "Assets",
      assetsTitle: "Brand & data assets",
      assets: [
        { label: "Official logo (JPG)", note: "High resolution — for print and web" },
        { label: "Company one-pager (PDF)", note: "Facts, stats and executive bios" },
        { label: "French Riviera data report (2026)", note: "Original research: prices, yields, foreign buyers" },
      ],
      locOverline: "Location",
      locTitle: "Where we operate",
      niceTitle: "Nice, France",
      niceDesc: "Primary operations. Team on the ground for property management, short-term rentals and concierge across the Côte d'Azur — from Cannes to Menton.",
      monacoTitle: "Monaco",
      monacoDesc: "Secondary office serving Monaco, Cap-d'Ail and the Larvotto corridor for ultra-prime clients and cross-border owners.",
      ctaTitle: "Working on a Riviera story?",
      ctaText: "We can share original data, on-record quotes, and access to owners from the US, Turkey and France for feature interviews.",
      ctaButton: "Email the press desk",
    },
  },

  tr: {
    meta: {
      homeTitle: "Velmira Living | Nice'te Butik Gayrimenkul ve Mülk Yönetimi",
      contactTitle: "Velmira Living İletişim | Nice ve Monaco Ofisleri",
    },
    nav: { home: "Ana Sayfa", properties: "Mülkler", services: "Hizmetler", about: "Hakkımızda", contact: "İletişim", shortTerm: "Kısa Dönem Kiralık", propertyMgmt: "Mülk Yönetimi", guides: "Rehberler", blog: "Blog", whatsapp: "WhatsApp" },
    hero: {
      overline: "Gayrimenkul · Mülk Yönetimi · Konsiyerj",
      title: "Riviera'daki eviniz,\nözenle yönetiliyor.",
      subtitle: "Velmira Living, Fransız Rivierası'nda butik bir gayrimenkul ve yaşam evidir — uluslararası mülk sahiplerine Nice ve çevresinde alım, satım ve sorunsuz mülk yönetiminde rehberlik eder.",
      cta1: "Mülkünüzün Gelir Potansiyelini Öğrenin",
      cta2: "Nice'te Gayrimenkul Arıyorum",
      cta3: "Satılık Gayrimenkulleri İnceleyin",
      ctaSupport: "Nice'teki mülkünüzün kısa ve orta dönem kiralama potansiyelini birlikte değerlendirelim. Yönetim, kiralama ve operasyon süreçlerini tek noktadan takip edin.",
    },
    stats: [
      { value: "25", label: "Yıl Toplam Sektör Deneyimi" },
      { value: "60+", label: "Yönetilen Mülk" },
      { value: "48", label: "Hizmet Verilen Mülk Sahibi" },
      { value: "4.9★", label: "Ortalama Misafir Puanı" },
    ],
    intents: {
      prompt: "Sizi Riviera'ya getiren nedir?",
      buy: "Ev satın almak istiyorum",
      own: "Burada zaten bir mülküm var",
      compare: "Yatırım destinasyonlarını karşılaştırıyorum",
    },
    buyPage: {
      seoTitle: "Nice ve Fransız Rivierası'nda Ev Satın Alma — Rehberli Arama | Velmira Living",
      seoDesc: "Bütçenizi, tercih ettiğiniz bölgeleri ve mülk tipini söyleyin. Yerel Velmira danışmanınız 48 saat içinde özenle seçilmiş bir kısa liste ile size dönsün.",
      overline: "Rehberli Alıcı Yolu",
      title: "Ne aradığınızı söyleyin.\nAramayı biz yapalım.",
      intro: "Üç hızlı seçim ve kısa bir form. Her mahalleyi yakından tanıyan bir Velmira danışmanı size bizzat yanıt verir — otomatik ilan bombardımanı yok, baskı yok.",
      budgetTitle: "Bütçeniz nedir?",
      budgetHint: "Noter masrafları hariç satın alma bedeli (ikinci el mülkte yaklaşık %7–8).",
      budgetRequired: "Lütfen önce bir bütçe aralığı seçin.",
      budgets: [
        { id: "lt300", label: "300 bin € altı" },
        { id: "300-500", label: "300 – 500 bin €" },
        { id: "500-1m", label: "500 bin – 1 milyon €" },
        { id: "1m-2m", label: "1 – 2 milyon €" },
        { id: "2m-5m", label: "2 – 5 milyon €" },
        { id: "5m+", label: "5 milyon € üzeri" },
      ],
      areaTitle: "Hangi bölgelerle ilgileniyorsunuz?",
      areaHint: "İstediğiniz kadar seçin — ya da bize bırakın.",
      areas: [
        { id: "carre-dor", label: "Nice — Carré d'Or" },
        { id: "mont-boron", label: "Nice — Mont Boron" },
        { id: "cimiez", label: "Nice — Cimiez" },
        { id: "promenade", label: "Nice — Promenade / Musiciens" },
        { id: "vieux-nice", label: "Vieux Nice (Eski Şehir)" },
        { id: "port", label: "Nice — Liman" },
        { id: "villefranche", label: "Villefranche / Cap Ferrat" },
        { id: "cagnes", label: "Cagnes-sur-Mer" },
        { id: "antibes", label: "Antibes / Juan-les-Pins" },
        { id: "cannes", label: "Cannes" },
        { id: "menton", label: "Menton" },
        { id: "monaco", label: "Monako" },
        { id: "other", label: "Diğer / henüz emin değilim" },
      ],
      typeTitle: "Ne tür bir mülk?",
      typeHint: "Bir veya birkaçını seçin.",
      types: [
        { id: "apartment", label: "Daire" },
        { id: "penthouse", label: "Çatı katı (Penthouse)" },
        { id: "villa", label: "Villa" },
        { id: "townhouse", label: "Müstakil şehir evi" },
        { id: "investment", label: "Yatırım / kiraya hazır" },
      ],
      formTitle: "Kısa listenizi nereye gönderelim?",
      formIntro: "48 saat içinde 3–6 özenle seçilmiş seçenek ve fiyatlar hakkında dürüst bir görüşle dönüyoruz.",
      note: "Bilmemiz gereken başka bir şey var mı? (isteğe bağlı)",
      notePlaceholder: "Örn: Deniz manzaralı ve teraslı 2 yatak odalı, tramvaya yürüme mesafesinde, 2026 sonbaharında taşınmak istiyoruz. Nakit alım; otopark şart.",
      noteHelp: "İşimize yarayan detaylar: yatak odası sayısı, deniz manzarası, teras, otopark veya asansör, finansman (nakit ya da kredi), zaman planınız.",
      submit: "Talebimi gönder",
      privacy: "Bilgileriniz yalnızca talebinize yanıt vermek için kullanılır. Bülten yok, üçüncü taraf yok.",
      summaryTitle: "Talebiniz",
      labels: { budget: "Bütçe", areas: "Bölgeler", type: "Mülk tipi", none: "Henüz seçilmedi" },
      trust: [
        "Bağımsız danışmanlık — hiçbir müteahhit veya acenteye bağlı değiliz",
        "Türkçe, İngilizce, Fransızca ve İspanyolca hizmet",
        "Anahtar tesliminden sonra da yanınızdayız: yönetim, kiralama ve concierge",
      ],
      successTitle: "Teşekkürler — talebiniz bize ulaştı.",
      successBody: "Bir Velmira danışmanı 48 saat içinde size bizzat dönecek. Hemen konuşmak ister misiniz? WhatsApp'tan ulaşın.",
      whatsapp: "WhatsApp'tan yazın",
      browseAll: "Tüm ilanlara göz atın",
    },
    ownerPage: {
      seoTitle: "Nice'teki Mülkünüz için Kira Geliri Tahmini | Velmira Living",
      seoDesc: "Nice'te zaten bir mülkünüz var mı? Changement d'usage durumu, mahalle, büyüklük ve hedeflerinizi paylaşın; 48 saat içinde kişisel bir kira geliri tahmini alın.",
      overline: "Nice'teki Mülk Sahipleri İçin",
      title: "Nice'te zaten bir mülkünüz var mı?\nNe kazandırabileceğine bakalım.",
      intro: "Beş hızlı adım. Bir Velmira danışmanı mülkünüzü bizzat inceler; gerçekçi bir gelir tahmini ve hedeflerinize uygun yönetim modeliyle size döner.",
      usageTitle: "Mülkün changement d'usage (kullanım değişikliği) izni var mı?",
      usageHint: "İlk kontrol ettiğimiz şey budur — hangi kiralama stratejisinin yasal olarak mümkün olduğunu belirler.",
      usageExplainer: "Nice'te ikinci konut niteliğindeki bir mülkü turistlere kısa süreli kiralamak için belediyeden 'changement d'usage' izni gerekir (şehir merkezinde telafi kuralları uygulanır). İzin yoksa kısa dönem kiralama sınırlıdır; eşyalı uzun dönem kiralama mümkündür.",
      usageRequired: "Lütfen önce changement d'usage durumunu belirtin.",
      usageOptions: [
        { id: "yes", label: "Evet, izin var" },
        { id: "no", label: "Hayır" },
        { id: "unsure", label: "Emin değilim" },
      ],
      usageYear: "İzin yılı (biliyorsanız)",
      areaTitle: "Mülk hangi mahallede?",
      areaHint: "Doluluk ve gecelik fiyatlar mahalleden mahalleye çok değişir.",
      areaRequired: "Lütfen mahalleyi seçin.",
      areas: [
        { id: "carre-dor", label: "Carré d'Or" },
        { id: "mont-boron", label: "Mont Boron" },
        { id: "cimiez", label: "Cimiez" },
        { id: "promenade", label: "Promenade / Musiciens" },
        { id: "vieux-nice", label: "Vieux Nice (Eski Şehir)" },
        { id: "port", label: "Liman (Port)" },
        { id: "liberation", label: "Libération" },
        { id: "riquier", label: "Riquier" },
        { id: "fabron", label: "Fabron / Californie" },
        { id: "other", label: "Diğer" },
      ],
      propertyTitle: "Mülkünüzden bahsedin.",
      propertyHint: "Yaklaşık rakamlar yeterli.",
      sizeLabel: "Kullanım alanı",
      bedroomsLabel: "Yatak odası",
      bedrooms: [
        { id: "studio", label: "Stüdyo" },
        { id: "1", label: "1 yatak odası" },
        { id: "2", label: "2 yatak odası" },
        { id: "3", label: "3 yatak odası" },
        { id: "4+", label: "4+ yatak odası" },
      ],
      goalsTitle: "Hedefleriniz ve beklentileriniz neler?",
      goalsHint: "Uyan her seçeneği işaretleyin.",
      goals: [
        { id: "income", label: "Kira gelirini artırmak" },
        { id: "outsource", label: "Mülk yönetimini tamamen devretmek" },
        { id: "part-year", label: "Yılın bir kısmında kendim kullanmak, kalanında kiraya vermek" },
        { id: "passive", label: "Minimum uğraşla pasif gelir elde etmek" },
        { id: "lt-to-st", label: "Uzun dönemden kısa dönem kiralamaya geçmek" },
        { id: "st-to-lt", label: "Kısa dönemden uzun dönem kiralamaya geçmek" },
        { id: "prepare", label: "Mülkü Airbnb / Booking.com için hazırlamak" },
        { id: "renovate", label: "Kiraya vermeden önce yenilemek veya iyileştirmek" },
        { id: "occupancy", label: "Doluluğu ve misafir yorumlarını iyileştirmek" },
        { id: "legal", label: "Kısa dönem kiralamanın yasal olarak mümkün olup olmadığını öğrenmek" },
        { id: "sell-later", label: "Gelir elde ederken ileride satmak" },
        { id: "advice", label: "Emin değilim — uzman görüşü istiyorum" },
      ],
      formTitle: "Tahmininizi nereye gönderelim?",
      formIntro: "48 saat içinde kişisel bir gelir tahmini ve önerilen yönetim modeli alırsınız. Rakamlar otomatik üretilmez — her talebi yerel bir danışman inceler.",
      note: "Mülk hakkında eklemek istediğiniz başka bir şey? (isteğe bağlı)",
      notePlaceholder: "Örn: Asansörlü 3. kat, deniz manzaralı teras, 2022'de yenilendi, haziran ayına kadar uzun dönem kirada. Yazın 6 hafta kendim kullanıyorum.",
      noteHelp: "İşimize yarayan detaylar: kat ve asansör, teras veya balkon, manzara, otopark, yenileme durumu, mevcut kiralama durumu, kendi kullandığınız haftalar.",
      submit: "Tahmin talep et",
      privacy: "Bilgileriniz yalnızca talebinize yanıt vermek için kullanılır. Bülten yok, üçüncü taraf yok.",
      summaryTitle: "Mülk özetiniz",
      labels: { usage: "Changement d'usage", area: "Mahalle", property: "Mülk", goals: "Hedefler", none: "Henüz seçilmedi" },
      trust: [
        "Nice ve Riviera boyunca 60'tan fazla yönetilen mülk",
        "Kısa dönem kiralama size uygun değilse bunu dürüstçe söyleriz",
        "Türkçe, İngilizce, Fransızca ve İspanyolca mülk sahibi raporlaması",
      ],
      successTitle: "Teşekkürler — mülk özetiniz bize ulaştı.",
      successBody: "Bir Velmira danışmanı inceleyip 48 saat içinde gelir tahmininizle size bizzat dönecek. Hemen konuşmak ister misiniz? WhatsApp'tan ulaşın.",
      whatsapp: "WhatsApp'tan yazın",
      learnMore: "Yönetim hizmetimiz nasıl işler",
    },
    investPage: {
      seoTitle: "Yatırım Destinasyonlarını Karşılaştırın — Nice vs Dubai, Lizbon, İstanbul | Velmira Living",
      seoDesc: "Nereye yatırım yapacağınızı karşılaştırıyor musunuz? Bütçenizi, hedeflerinizi ve vadenizi söyleyin; 48 saat içinde Nice'in değerlendirdiğiniz destinasyonlara karşı getiri odaklı, vergi sonrası karşılaştırmasını alın.",
      overline: "Yatırımcı Yolu",
      title: "Destinasyonları karşılaştırıyor musunuz?\nBroşür değil, getiri konuşalım.",
      intro: "Beş hızlı adım ve kısa bir form. Bir Velmira danışmanı bütçeniz için dürüst, getiri odaklı bir karşılaştırmayla döner — Nice'in kazanmadığı noktalar dahil.",
      destTitle: "Hangi destinasyonları karşılaştırıyorsunuz?",
      destHint: "Listenizdeki tüm pazarları seçin.",
      destinations: [
        { id: "nice", label: "Nice / Côte d'Azur" },
        { id: "dubai", label: "Dubai" },
        { id: "lisbon", label: "Lizbon" },
        { id: "istanbul", label: "İstanbul" },
        { id: "bodrum", label: "Bodrum" },
        { id: "athens", label: "Atina" },
        { id: "london", label: "Londra" },
        { id: "other", label: "Diğer" },
      ],
      budgetTitle: "Yatırım bütçeniz nedir?",
      budgetHint: "Alım maliyetleri hariç, mülk başına toplam satın alma bütçesi.",
      budgetRequired: "Lütfen önce bir bütçe aralığı seçin.",
      budgets: [
        { id: "lt300", label: "300 bin € altı" },
        { id: "300-500", label: "300 – 500 bin €" },
        { id: "500-1m", label: "500 bin – 1 milyon €" },
        { id: "1m-2m", label: "1 – 2 milyon €" },
        { id: "2m-5m", label: "2 – 5 milyon €" },
        { id: "5m+", label: "5 milyon € üzeri" },
      ],
      objectiveTitle: "Sizin için en önemlisi ne?",
      objectiveHint: "Uyan tüm seçenekleri işaretleyin — karşılaştırmayı buna göre ağırlıklandırırız.",
      objectiveRequired: "Lütfen en az bir hedef seçin.",
      objectives: [
        { id: "str-yield", label: "Kısa dönem kira getirisi" },
        { id: "lt-rent", label: "İstikrarlı uzun dönem kira" },
        { id: "appreciation", label: "Değer artışı" },
        { id: "residency", label: "Oturum / vize yolu" },
        { id: "lifestyle", label: "Yaşam + gelir (yılın bir kısmında kendim kullanmak)" },
        { id: "diversify", label: "Döviz / varlık çeşitlendirmesi" },
        { id: "portfolio", label: "Çok üniteli portföy oluşturmak" },
      ],
      horizonTitle: "Yatırım vadeniz nedir?",
      horizonHint: "Getiri ile değer artışı ve likiditeyi dengelememize yardımcı olur.",
      horizons: [
        { id: "lt3", label: "3 yıldan az" },
        { id: "3-7", label: "3 – 7 yıl" },
        { id: "7+", label: "7+ yıl / nesiller arası" },
      ],
      involvementTitle: "Ne kadar dahil olmak istersiniz?",
      involvements: [
        { id: "hands-off", label: "Tamamen elimi çekmek" },
        { id: "light", label: "Hafif katılım" },
        { id: "active", label: "Aktif katılım" },
      ],
      formTitle: "Karşılaştırmayı nereye gönderelim?",
      formIntro: "48 saat içinde Nice'in listenizle getiri odaklı karşılaştırmasını, vergi mukimliğinize göre brütten nete hesabı ve uygun mahalleleri alırsınız.",
      countryLabel: "Vergi mukimliği ülkeniz",
      countryPlaceholder: "Örn: Türkiye, ABD, Almanya",
      countryHelp: "Net getiri, Fransa ile vergi anlaşmanıza bağlıdır — hesaba katarız.",
      note: "Başka bir şey? (isteğe bağlı)",
      notePlaceholder: "Örn: Her biri yaklaşık 600 bin € olan 2 daire arıyorum, kısa dönem kiralamayla tamamen elimi çekmek istiyorum, alternatif olarak Dubai proje aşaması düşünüyorum.",
      submit: "Talebimi gönder",
      privacy: "Bilgileriniz yalnızca talebinize yanıt vermek için kullanılır. Bülten yok, üçüncü taraf yok.",
      summaryTitle: "Yatırımcı özetiniz",
      labels: { destinations: "Karşılaştırılan", budget: "Bütçe", objectives: "Hedefler", horizon: "Vade · katılım", none: "Henüz seçilmedi" },
      glance: {
        title: "Nice bir bakışta — 2026",
        items: { yield: "Ort. brüt kira getirisi", growth: "Medyan fiyat artışı (yıllık)", occ: "Kısa dönem doluluk (merkez)", adr: "Ort. gecelik fiyat", median: "Medyan 1+1 işlem", notaire: "Noter masrafı (ikinci el)" },
        note: "2026 Riviera Veri Raporu ve yönettiğimiz portföyden gösterge rakamlar. Danışmanınız hedef mahalleniz için netleştirir.",
        link: "Veri raporunun tamamını okuyun",
      },
      trust: [
        "Dürüst karşılaştırırız — Nice'in kazanmadığı noktalar dahil",
        "Vergi mukimliğiniz hesaba katılarak brütten nete getiri",
        "Satın almadan yönetim ve kiralamaya tek partner",
      ],
      successTitle: "Teşekkürler — yatırımcı özetiniz bize ulaştı.",
      successBody: "Gelen kutunuzu kontrol edin: Nice getiri özetimizi ve karşılaştırma notlarını gönderdik. Bir Velmira danışmanı 48 saat içinde bizzat dönecek.",
      whatsapp: "WhatsApp'tan yazın",
      readReport: "2026 veri raporunu okuyun",
    },
    flowThanks: {
      greeting: "Teşekkürler, {name}",
      readTitle: "Beklerken göz atın",
      steps: {
        buy: [
          { title: "Onay", body: "Talebiniz kaydedildi ve seçtiğiniz bölgeleri yakından tanıyan bir danışmana atandı." },
          { title: "Kişisel kısa liste", body: "48 saat içinde fiyat ve pazarlık payı hakkında dürüst bir görüşle birlikte 3–6 özenle seçilmiş seçenek alırsınız." },
          { title: "Görüntüleme", body: "Yerinde veya görüntülü görüntüleme ayarlarız; noter, finansman ve anahtar teslimine kadar yanınızdayız." },
        ],
        owner: [
          { title: "Onay e-postası", body: "Gelen kutunuzu kontrol edin — 2026 changement d'usage rehberinizi ve talep özetinizi gönderdik." },
          { title: "Danışman incelemesi", body: "Bir Velmira danışmanı mahallenizi, izin durumunuzu ve hedeflerinizi inceler — otomatik rakam yok." },
          { title: "Gelir tahmini", body: "48 saat içinde gerçekçi bir gelir aralığı ve size uygun yönetim modelini alırsınız; hiçbir yükümlülük yok." },
        ],
        invest: [
          { title: "Getiri özeti gönderildi", body: "Gelen kutunuzu kontrol edin — Nice getiri özetimiz ve destinasyonlarınız için karşılaştırma notları orada." },
          { title: "Danışman incelemesi", body: "Bütçenizi, hedeflerinizi ve vergi mukimliğinizi değerlendirip her pazar için brütten nete bir tablo çıkarırız." },
          { title: "Karşılaştırmanız", body: "48 saat içinde uygun mahalleler, getiri hesabı ve net bir teklif alırsınız — hiçbir yükümlülük yok." },
        ],
      },
      reads: {
        buy: [
          { to: "/blog/how-much-are-notaire-closing-costs-in-nice-in-2026", label: "2026'da Nice'te noter masrafları ne kadar?" },
          { to: "/blog/what-should-a-foreign-buyer-check-when-viewing-a-home-in-nice", label: "Yabancı bir alıcı Nice'te ev gezerken neleri kontrol etmeli?" },
          { to: "/properties", label: "Güncel ilanlarımıza göz atın" },
        ],
        owner: [
          { to: "/blog/how-to-legally-rent-out-a-furnished-home-to-tourists-in-nice-in-2026", label: "2026'da Nice'te eşyalı bir evi turistlere yasal olarak nasıl kiralarsınız" },
          { to: "/blog/what-rental-yield-can-you-expect-from-an-apartment-in-nice-in-2026", label: "2026'da Nice'te bir daireden ne kadar kira getirisi beklenir?" },
          { to: "/property-management", label: "Velmira mülk yönetimi nasıl işler" },
        ],
        invest: [
          { to: "/riviera-real-estate-data-2026", label: "2026 Riviera Gayrimenkul Veri Raporu" },
          { to: "/fransa-gayrimenkul-yatirimi", label: "Fransa'da gayrimenkul yatırımı rehberi" },
          { to: "/blog/what-rental-yield-can-you-expect-from-an-apartment-in-nice-in-2026", label: "2026'da Nice'te bir daireden ne kadar kira getirisi beklenir?" },
        ],
      },
    },
    about: {
      overline: "Biz Kimiz",
      title: "Netlik, güvenilirlik ve güven — gayrimenkul böyle hissettirmeli.",
      p1: "Fransız Rivierası'nda değerli bir mülke sahip olmak zahmetsiz olmalı. Gerçekte ise nadiren öyledir: uzaktan sahiplik, güvenilmez hizmet sağlayıcılar ve kaybolan kira geliri varlığınızı sessizce eritir.",
      p2: "Biz bunu düzeltiriz. Satın almadan kira performansına kadar, Velmira Living tüm süreci disiplin, şeffaflık ve tutarlı iletişimle yönetir.",
      p3: "ABD, Türkiye, Fransa ve İtalya dahil olmak üzere farklı ülkelerden uluslararası mülk sahipleriyle çalışıyoruz. İlişkilerimizi güven, dürüstlük ve özen üzerine inşa ediyoruz.",
    },
    services: {
      overline: "Ne Yapıyoruz",
      title: "İlk görüşmeden günlük bakıma kadar uçtan uca destek.",
      items: [
        { title: "Alım & Satım", desc: "Bulma, pazarlık ve tamamen rehberli bir işlem — her adımda, sizin dilinizde." },
        { title: "Satın Alma Desteği", desc: "Alımınızın yapılandırılmış ve güvenli olması için hukuki, noter ve finansman koordinasyonu." },
        { title: "Renovasyon Koordinasyonu", desc: "Güvenilir müteahhitler, şeffaf bütçeler ve uygulamalı proje yönetimi." },
        { title: "Kısa Dönem Kiralama Kurulumu", desc: "Sadece ilan vermiyoruz — dinamik fiyatlandırma ile geliri optimize ediyoruz." },
        { title: "Mülk Yönetimi", desc: "Yerel varlık yöneticiniz: denetimler, bakım ve yıl boyunca koruma." },
        { title: "Konsiyerj Hizmetleri", desc: "Otel kalitesinde misafir deneyimleri, VIP karşılamalar ve kişiye özel yaşam." },
      ],
    },
    featured: {
      overline: "Öne Çıkan Portföy",
      title: "Evler ve konaklamalardan bir seçki.",
      subtitle: "Nice ve Riviera genelinde özenle seçilmiş satılık mülkler ve premium kısa dönem kiralıklar.",
      viewAll: "Tüm Mülkleri Gör",
    },
    values: {
      overline: "Sözümüz",
      title: "Üç ilke üzerine kurulu.",
      items: [
        { title: "Şeffaflık", desc: "Her adımda tam görünürlük — güvenebileceğiniz raporlar, bütçeler ve kararlar." },
        { title: "Kalite", desc: "Riviera'ya yakışan standartlar: otel kalitesinde hizmet ve titizlikle bakılan varlıklar." },
        { title: "Güven", desc: "Yatırımınız, kendimizinmiş gibi yönetilir — korunur ve uzun vadede optimize edilir." },
      ],
    },
    testimonials: {
      overline: "Müşteri Sesleri",
      ratingLine: "Mülk sahibi & misafir ortalama puanı · 56 yayınlanmış yorum · Airbnb Superhost",
      title: "Üç kıtadaki mülk sahiplerinin güveni.",
      items: [
        { quote: "Velmira Living, Nice'teki mülkümüzü bir yıldan uzun süredir yönetiyor. Tüm ekipleri profesyonel ve bizimle ve misafirlerimizle sürekli iletişim halinde. Velmira olmadan mülkümüz zirvede olmazdı.", author: "Radford Ailesi", location: "Nice" },
        { quote: "Nice'teki dairemizi yönetmek için Velmira Living'i bulduğumuz için çok şanslıyız ve mülkümüze önde gelen bir kısa dönem kiralama platformunda 'Guest Favorite' puanı kazandırmalarına şaşırmadık. Vazgeçilmez bir ortak.", author: "Mark Ailesi", location: "Nice" },
        { quote: "Velmira Living muhteşem bir yönetim şirketi. Yönetimleri altında mülkümüz sürekli kiralanıyor ve beş yıldız yorumlar alıyor — Airbnb'de listelendiğimizden beri her çeyrek Superhost. Verebilseydim altı yıldız verirdim.", author: "Sousa Ailesi", location: "Nice" },
        { quote: "Nice'teki mülkümüz için bu ekibin verdiği hizmetten daha memnun olamazdık. Olağanüstü profesyonellik, her misafire özenli ilgi ve her ayrıntı çok yüksek standartta yönetiliyor.", author: "Özilhan Ailesi", location: "Nice" },
        { quote: "Velmira Living, Nice'teki dairemizle yıllardır ilgileniyor ve daha memnun olamazdık. Her misafire özen ve empatiyle davranıyor, dairemize kendi evleriymiş gibi bakıyorlar.", author: "Misuri Ailesi", location: "Nice" },
        { quote: "Velmira Living'i çekinmeden tavsiye ederim. Nice'teki dairemi yönetmeye başladıklarından beri tamamen memnunum — profesyonellikleri, hızları ve güvenilirlikleri tam güven veriyor.", author: "Bağdadı Ailesi", location: "Nice" },
      ],
    },
    team: {
      overline: "Ekibimiz",
      title: "Riviera evinizin arkasındaki insanlar.",
      subtitle: "Küçük, deneyimli bir ekip — her biri mülkünüzün bir alanından sorumlu. Aktarma yok, sorumluluk sulandırılmıyor.",
      members: [
        { name: "Gorkem", role: "Kurucu & Operasyon Direktörü", photo: "/team/gorkem.jpg" },
        { name: "Gozde", role: "Gayrimenkul & Hukuki İşler", photo: "/team/gozde.jpg" },
        { name: "Raju", role: "Bakım & Teknik Operasyonlar", photo: "/team/raju.jpg" },
        { name: "Maria", role: "Temizlik Operasyonları", photo: "/team/maria.jpg" },
      ],
    },
    howItWorks: {
      overline: "Nasıl Çalışır",
      title: "Üç hizmet. Tek şeffaf süreç.",
      subtitle: "Alım, kiralama veya mevcut mülkünüzü emanet etme — adımlar net, yazılı olarak kabul edilmiş ve ekibimizden isimli bir kişi tarafından yürütülür.",
      tracks: [
        {
          key: "buy",
          title: "Velmira ile Gayrimenkul Alımı",
          steps: [
            { title: "Tanışma görüşmesi", desc: "Bütçenizi, zaman planınızı, kullanım amacınızı (kişisel, kiralama, karma) ve Nice ile Riviera'da tercih ettiğiniz semtleri belirliyoruz." },
            { title: "Özenle seçilmiş kısa liste", desc: "Uygun mülkleri elle seçiyoruz — mümkün olduğunda piyasa dışı — ve dürüst getiri, durum ve yeniden satış notlarını paylaşıyoruz." },
            { title: "Görüntüleme & durum tespiti", desc: "Yerinde veya sanal görüntülemeler, ayrıca bina, hukuki ve noter kontrolleri — böylece sizi hiçbir şey şaşırtmaz." },
            { title: "Teklif & müzakere", desc: "Fiyatı, koşulları ve takvimi kendi dilinizde müzakere ediyor, compromis ve finansmanı koordine ediyoruz." },
            { title: "Noter & anahtarlar", desc: "Vekaletname ile noter imzasına sizin adınıza katılıyor ve mülkü ilk günden kullanıma veya kiralamaya hazırlıyoruz." },
          ],
        },
        {
          key: "rental",
          title: "Kısa Dönem Kiralama Yönetimi",
          steps: [
            { title: "Mülk denetimi", desc: "Mülkü ziyaret ediyor, kazanç potansiyelini ölçüyor ve dönüşüm sağlayan mobilya, fotoğraf ve ilan iyileştirmeleri öneriyoruz." },
            { title: "Kurulum & ilanlar", desc: "Editoryal fotoğraflar, profesyonel metin ve haftalık dinamik fiyatlandırma ile çok platformlu ilanlar (Airbnb, Booking, doğrudan)." },
            { title: "Misafir operasyonları", desc: "7/24 mesajlaşma, tarama, giriş, karşılama, yerel öneriler — otel maliyeti olmadan otel deneyimi." },
            { title: "Temizlik & çamaşır", desc: "Hastane seviyesinde temizlik, otel kalitesinde çamaşırlar, her konaklama arasında stok yenileme ve kalite kontrolleri." },
            { title: "Raporlama & ödemeler", desc: "Net gelir, gider, doluluk ve yorumlarla aylık mülk sahibi raporu — sürpriz yok." },
          ],
        },
        {
          key: "management",
          title: "Mülk Sahipleri için Mülk Yönetimi",
          steps: [
            { title: "Onboarding", desc: "Mülkü inceliyor, durumunu belgeliyor ve elektrik/su, sigorta ve bina yönetimi iletişimlerini devralıyoruz." },
            { title: "Önleyici bakım", desc: "Planlı kontroller, mevsimlik bakım ve tedarikçi koordinasyonu ile mülkü yıl boyu koruyoruz." },
            { title: "Anlık müdahale", desc: "Sizin, yöneticinizin ve tüm ustaların tek numara üzerinden ulaşabildiği bir merkez — günler değil saatler içinde harekete geçiyoruz." },
            { title: "Sahip konaklamaları", desc: "Ziyaretleriniz için mülkü hazırlıyoruz — erzak, temizlik, transferler, konsiyerj ekstraları — hazır bir eve varırsınız." },
            { title: "Yıllık değerlendirme", desc: "Fotoğraflı yıllık gezi, önerilen işler ve önümüzdeki 12 aylık yazılı bir plan." },
          ],
        },
      ],
    },
    cta: {
      title: "Riviera'yı ev yapmaya hazır mısınız?",
      subtitle: "Bir sonraki alımınıza, satışınıza veya kiralamanıza netlik ve özenle rehberlik edelim.",
      button: "Görüşmeyi Başlatın",
    },
    caseStudies: {
      overline: "Mülk Sahibi Vaka Çalışmaları",
      title: "Gerçek mülkler. Gerçek rakamlar.",
      subtitle: "Üç anonim mülk özeti — devraldıktan sonra belgelenmiş doluluk, ADR ve gelir artışlarıyla.",
      challenge: "Zorluk",
      actions: "Velmira ne yaptı",
      results: "Sonuçlar",
      managedSince: "Yönetim başlangıcı",
      owner: "Mülk sahibi",
      from: "önce",
      to: "sonra",
    },
    footer: {
      tagline: "Fransız Rivierası'nda butik gayrimenkul ve yaşam.",
      quickLinks: "Keşfet",
      contactTitle: "İletişim",
      rights: "Tüm hakları saklıdır.",
      based: "Nice, Fransa merkezli",
      follow: "Bizi takip edin",
      legalLink: "Yasal Bilgiler",
      legalTitle: "Yasal bilgiler",
    },
    legal: {
      title: "Yasal Bilgiler",
      subtitle: "Şirket bilgileri ve web sitesi bildirimleri",
      publisherTitle: "Yayıncı",
      publisherText: "Bu web sitesi, Fransa'da kayıtlı bir şirket olan Velmira Living tarafından yayınlanmaktadır.",
      identifiersTitle: "Şirket kimlik bilgileri",
      contactTitle: "İletişim",
      hostingTitle: "Barındırma",
      hostingText: "Bu web sitesi güvenli, yönetilen bir altyapı üzerinde barındırılmaktadır. Barındırma ile ilgili talepler yukarıdaki iletişim adresine gönderilebilir.",
      contentTitle: "İçerik, fikri mülkiyet ve veriler",
      contentText: "Bu web sitesindeki tüm içerik — metinler, fotoğraflar, grafikler, logolar ve ticari markalar — aksi belirtilmedikçe Velmira Living'in münhasır mülkiyetindedir. Önceden yazılı izin alınmadan tamamen veya kısmen kopyalanması, sunulması veya dağıtılması kesinlikle yasaktır. Formlar aracılığıyla toplanan kişisel veriler, GDPR uyarınca işlenmekte ve yalnızca talebinize yanıt vermek için kullanılmaktadır.",
      updated: "Son güncelleme",
      cookiesTitle: "Çerezler ve ziyaretçi analizi",
      cookiesIntro: "Bu web sitesi, ziyaretçilerin sayfalarımızı nasıl kullandığını anlamak (trafik istatistikleri, gezinme yolları, sayfa ve form performansı) ve sayfaları iyileştirmek için iki ziyaretçi analizi hizmeti kullanır. Bu araçlar yalnızca analizi kabul etmenizden sonra yüklenir. Reddederseniz hiçbir analiz betiği yüklenmez ve bu sağlayıcılara hiçbir istek gönderilmez. Formlarımıza girdiğiniz bilgiler bu araçlara hiçbir zaman iletilmez.",
      cookiesServicesTitle: "Kullanılan hizmetler",
      cookiesServices: [
        { name: "Google Analytics 4 (Google Ireland Ltd)", purpose: "Ziyaretçi istatistikleri ve dönüşüm ölçümü (ör. bir form üzerinden gönderilen talep). IP adresleri Google Analytics 4 tarafından saklanmaz.", cookies: "_ga ve _ga_* çerezleri — süresi 13 ayla sınırlıdır.", link: "https://policies.google.com/privacy", linkLabel: "Google gizlilik politikası" },
        { name: "Microsoft Clarity (Microsoft Ireland Operations Ltd)", purpose: "Sayfaların nasıl kullanıldığını anlamak için ısı haritaları ve anonimleştirilmiş oturum kayıtları. Formlara yazılan içerik maskelenir.", cookies: "_clck (en fazla 12 ay) ve _clsk (24 saat) çerezleri. Oturum kayıtları Microsoft tarafından 30 gün, ısı haritaları 9 ay saklanır.", link: "https://privacy.microsoft.com/privacystatement", linkLabel: "Microsoft gizlilik bildirimi" },
      ],
      cookiesEssentialTitle: "Zorunlu teknolojiler (onay gerektirmez)",
      cookiesEssentialText: "Yalnızca kesinlikle gerekli depolama: dil tercihiniz, sohbet oturum kimliğiniz ve çerez tercihinizin kaydı (tarayıcınızda yerel olarak 6 ay saklanır) ile yönetim alanıyla sınırlı güvenli bir oturum çerezi. Bunlar sizi siteler arasında izlemez.",
      cookiesChoiceTitle: "Tercihiniz",
      cookiesChoiceText: "İlk ziyaretinizde bir bant iki eşdeğer seçenek sunar: \"Analizi kabul et\" veya \"Yalnızca gerekli\". Tercihinizi dilediğiniz zaman sayfa alt bilgisindeki \"Çerez ayarları\" bağlantısından veya aşağıdaki düğmeden değiştirebilir ya da geri çekebilirsiniz. Onayın geri çekilmesi ziyaretçi analizini derhal durdurur ve bu alan adında yerleştirilen analiz çerezlerini siler; tarayıcı ayarlarınızdan da çerezleri her zaman silebilirsiniz.",
      cookiesButton: "Çerez ayarlarını aç",
    },
    properties: {
      title: "Mülklerimiz",
      subtitle: "Fransız Rivierası'nda satılık evler ve premium kısa dönem kiralıklar.",
      all: "Tümü", sale: "Satılık", rental: "Kısa Dönem Kiralık",
      from: "Başlangıç", perNight: "/ gece", midTerm: "Orta Dönem Kiralık", perMonth: "/ ay", viewDetails: "Detayları Gör",
      empty: "Bu filtreye uygun mülk henüz yok.",
      valuationTitle: "Nereden başlayacağınızı bilmiyor musunuz?",
      valuationText: "Ücretsiz bir değerleme veya danışma randevusu alın. Hedeflerinizi, mülkünüzü ve kazanç potansiyelini birlikte değerlendirelim — yükümlülük yok.",
      valuationBtn: "Değerleme / Danışma Randevusu Alın",
      beds: "yatak", baths: "banyo",
    },
    detail: {
      back: "Mülklere Dön",
      forSale: "Satılık", forRent: "Kısa Dönem Kiralık", forRentMidTerm: "Orta Dönem Kiralık · Aylık", stayRange: "{min} ile {max} ay arası konaklama",
      overview: "Genel Bakış", features: "Özellikler & Olanaklar",
      bedrooms: "Yatak Odası", bathrooms: "Banyo", area: "Yaşam Alanı",
      inquire: "Bu Mülk Hakkında Bilgi Al",
      perNight: "gece başına", perMonth: "aylık",
      loading: "Yükleniyor…",
      notFound: "Mülk bulunamadı.",
    },
    contact: {
      overline: "İletişime Geçin",
      title: "Riviera planlarınızı konuşalım.",
      subtitle: "Alım, satım, kiralama veya yönetim — neye ihtiyacınız olduğunu söyleyin, kişisel olarak yanıtlayalım.",
      name: "Ad Soyad", email: "E-posta", phone: "Telefon / WhatsApp",
      interest: "İlgilendiğim konu", message: "Mesaj",
      interestOptions: {
        general: "Genel soru", buy: "Mülk satın alma", sell: "Mülk satma",
        rent: "Kiralama (kısa dönem)", rent_mid: "Aylık kiralama (1–11 ay)", manage: "Mülk yönetimi", concierge: "Konsiyerj hizmetleri",
        valuation: "Değerleme / danışma randevusu",
      },
      submit: "Mesaj Gönder",
      sending: "Gönderiliyor…",
      success: "Teşekkürler — mesajınız alındı. En kısa sürede sizinle iletişime geçeceğiz.",
      error: "Bir şeyler ters gitti. Lütfen tekrar deneyin veya WhatsApp'tan ulaşın.",
      reachTitle: "Bize doğrudan ulaşın",
      whatsappText: "Merhaba Velmira Living, hizmetleriniz hakkında daha fazla bilgi almak istiyorum.",
      emailLabel: "E-posta", whatsappLabel: "WhatsApp", locationLabel: "Konum",
      location: "4B Avenue Durante, 06000 Nice, France",
      locationMonaco: "7 Place Princesse Gabriella, 98000 Monaco",
      hoursLabel: "Çalışma Saatleri",
      hoursWeekdays: "Pzt – Cum · 09:00 – 19:00",
      hoursSaturday: "Cumartesi · 10:00 – 17:00",
      hoursSunday: "Pazar · Kapalı",
      mapTitle: "Nice ofisimize ulaşın",
    },
    aboutPage: {
      overline: "Velmira Living Hakkında",
      title: "Ortada bir iş ortağı — performansın gerçekleştiği yer.",
      intro: "Fransız Rivierası'nda değerli bir mülke sahip olmak zahmetsiz olmalı. Satın almadan kira performansına kadar tüm süreci netlik, disiplin ve tutarlı iletişimle yönetiriz.",
      whyOverline: "Neden Velmira",
      whyTitle: "Ne çok küçük ne çok büyük. Tam olarak sonuçların doğduğu yer.",
      whyIntro: "Çoğu şirket ya:",
      whyContrast: ["Çok küçük → güvenilmez", "Çok büyük → kişiliksiz"],
      whyMiddle: "Biz ortada, performansın gerçekleştiği yerde faaliyet gösteririz.",
      whyPoints: [
        "Côte d'Azur'da yerel uzmanlık",
        "Birden fazla üniteyi yöneten gerçek operasyonel deneyim",
        "Gelir odaklı bakış açısı",
        "Uygulamalı icra",
      ],
      whyClosing: "Sorumluluğu dışarıya devretmeyiz.",
      careOverline: "Bizi Tanıyın",
      careTitle: "Eksiksiz Mülk Bakımı. Sıfır Sürtünme. Maksimum Getiri.",
      careP1: "Mülkünüzün her yönünü yönetiriz, böylece siz uğraşmazsınız.",
      careP2: "Günlük operasyonlardan gelir optimizasyonuna kadar, Velmira varlığınızın korunmasını, bakımını ve tam potansiyelinde performans göstermesini sağlar.",
      carePoints: [
        "Tam mülk gözetimi",
        "Kısa dönem kiralama optimizasyonu",
        "Misafir yönetimi ve konsiyerj",
        "Temizlik ve devir operasyonları",
        "Bakım ve acil durum yönetimi",
        "Hukuki ve idari koordinasyon",
      ],
      careClosing: "Tek ortak. Tam kontrol. Operasyonel yük yok.",
      brandStoryOverline: "Felsefemiz",
      brandStory: "Velmira Living olarak, butik gayrimenkul ve yaşam danışmanlığımız aracılığıyla Akdeniz'in yaşam sanatını rafine bir yatırım vizyonuyla buluşturuyoruz. İsmimiz, akışkanlık ve zarif sadeliği çağrıştıran \"Vel\" ile hayranlık ve güzelliği simgeleyen \"Mira\" kelimelerinin birleşiminden doğar.",
      ctaButton: "Danışma Randevusu Alın",
    },
    pmPage: {
      seoTitle: "Nice Mülk Yönetimi ve Gayrimenkul Hizmetleri | Velmira Living",
      seoDescription: "Yurt dışındaki sahipler için Nice'te mülk yönetimi: mülk gözetimi ve bakımı, bakım koordinasyonu, kısa dönem kiralama destek hizmetleri ve şeffaf raporlama.",
      overline: "Nice Mülk Yönetimi",
      title: "Nice'te Mülk Yönetimi ve Gayrimenkul Hizmetleri",
      intro: "Fransa'da yaşamayan mülk sahipleri için mülk gözetimi, bakım koordinasyonu, sahip desteği ve kısa dönem kiralama konsiyerj hizmetleri — yerel icrayı şeffaf ve güvenilir gözetimle birleştirerek, siz yerinde olmadan tam kontrolü elinizde tutmanızı sağlıyoruz.",
      servicesTitle: "Yatırım Hizmetlerimiz",
      services: [
        { title: "Mülk Bulma", desc: "Yatırım hedeflerinize ve tercihlerinize uygun mülkleri titizlikle belirleriz, böylece bilinçli satın alma kararları verirsiniz." },
        { title: "Satın Alma Desteği", desc: "Pazarlıktan tapuya kadar ekibimiz, en iyi anlaşmayı sağlamak için her adımda size rehberlik eder." },
        { title: "Renovasyon Koordinasyonu", desc: "Konseptten teslime kadar, özenle seçilmiş yerel yüklenicilerle özgün renovasyonları koordine ediyor; yaşam tarzınızı zenginleştiren, mülkünüzün uzun vadeli değerini artıran ve özel konut, tatil evi ya da yüksek performanslı bir kiralık yatırım olarak çekiciliğini en üst düzeye çıkaran zarif, işlevsel ve zamansız iç mekânlar yaratıyoruz." },
        { title: "Kısa Dönem Kiralama Kurulumu", desc: "Mülkünüzü kısa dönem kiralama pazarında başarıya taşımak için özgün iç tasarım, profesyonel ilan yönetimi ve misafir çekiciliğini, doluluğu ve geliri en üst düzeye çıkarmak üzere kişiye özel bir strateji sunuyoruz." },
        { title: "Mülk Gözetimi ve Bakımı", desc: "Misafir ilişkilerinden denetimlere ve bakım koordinasyonuna kadar günlük işleyişi biz üstleniriz, operasyonel yükü sizden alırız." },
        { title: "Konsiyerj Hizmetleri", desc: "Kişiye özel konsiyerj hizmetleri misafir deneyimini geliştirir, memnuniyeti ve tekrar kiralamaları artırır." },
      ],
      intlTitle: "Uluslararası müşterilere nasıl yardımcı oluyoruz",
      intlIntro: "Yerinde olmadan tam görünürlük ve kontrole ihtiyaç duyan mülk sahipleri için tasarlandı.",
      intlPoints: [
        { title: "Yerel Temsil", desc: "Nice'te güvenilir irtibat noktanız olarak hareket eder; sizin adınıza acenteler, noterler, bina yöneticileri ve hizmet sağlayıcılarla koordinasyon sağlarız." },
        { title: "Açık ve Tutarlı İletişim", desc: "Güncellemeler düzenli ve özdür. Mülkünüz, maliyetler ve performans hakkında her zaman bilgi sahibi olursunuz." },
        { title: "Satın Alma ve Kurulum", desc: "Bulmadan durum tespitine ve evi kullanıma veya kiraya hazırlamaya kadar süreci güvenilir bir yerel ağ ile yönetiriz." },
        { title: "Operasyonel Yönetim", desc: "Bakım koordinasyonu, denetimler, temizlik ve tedarikçi gözetimi — şeffaf raporlama ile." },
        { title: "Kısa Dönem Kiralama Destek Hizmetleri", desc: "Kısa ve orta dönem kiralamalar için ilanları, fiyatlandırmayı, misafir iletişimini, giriş/çıkışı ve sorun çözümünü yönetiriz." },
        { title: "Finansal Görünürlük", desc: "Yapılandırılmış raporlama, gelir, gider ve genel performansa dair net bir görünüm sunar." },
        { title: "Konsiyerj ve Yerinde Destek", desc: "Gerektiğinde varışlar, hizmetler ve yerel düzenlemelerde size ve misafirlerinize yardımcı oluruz." },
      ],
      benefitsTitle: "Bu size nasıl fayda sağlar",
      benefits: [
        "Yerinde olmanıza gerek kalmadan kontrolü elinizde tutarsınız",
        "Kararlar tahminle değil, net bilgiyle alınır",
        "Mülkünüz korunur ve tutarlı bir şekilde yönetilir",
        "Kira performansı izlenir ve optimize edilir",
      ],
      benefitsClosing: "Kısacası, yerel icrayı şeffaf ve güvenilir gözetimle birleştirerek Fransa'da mülk sahipliğini basitleştiririz.",
      brandStoryOverline: "Felsefemiz",
      brandStory: "Velmira Living olarak, butik gayrimenkul ve yaşam danışmanlığımız aracılığıyla Akdeniz'in yaşam sanatını rafine bir yatırım vizyonuyla buluşturuyoruz. İsmimiz, akışkanlık ve zarif sadeliği çağrıştıran \"Vel\" ile hayranlık ve güzelliği simgeleyen \"Mira\" kelimelerinin birleşiminden doğar.",
      comparison: {
        overline: "Karşılaştırma",
        title: "Velmira Living, geleneksel bir acente ve kendi kendine yönetilen platform arasındaki fark.",
        intro: "Geleneksel bir Riviera acentesi veya kendi kendine yönetilen bir platform yerine butik yerel bir ortak seçtiğinizde neyin değiştiğinin dürüst bir dökümü. 2026'da Nice / Fransız Rivierası pazarındaki tipik hizmetleri yansıtır.",
        featureLabel: "Özellik",
        columns: ["Velmira Living", "Geleneksel Riviera Acentesi", "Kendi Kendine Yönetilen Platformlar"],
        rows: [
          { label: "Ortalama misafir yanıt süresi", values: ["<15 dk, 7/24", "1 – 4 saat (mesai saatleri)", "4 – 24 saat (otomatik)"] },
          { label: "Özel müşteri yöneticisi", values: ["✔ Özel mülk yöneticisi", "Bazen", "✘"] },
          { label: "Diller", values: ["İngilizce • Fransızca • Türkçe • İspanyolca", "Ağırlıklı olarak Fransızca", "Otomatik çok dilli destek"] },
          { label: "Yerel varlık", values: ["Nice merkezli operasyon + Monaco ofisi", "Yerel ofis", "Yerel ekip yok"] },
          { label: "Misafir doğrulama", values: ["Manuel inceleme + kimlik + rezervasyon değerlendirmesi", "Kimlik doğrulama", "Yalnızca otomatik kontroller"] },
          { label: "Fiyatlandırma stratejisi", values: ["Haftalık dinamik fiyat + rakip kıyaslaması", "Periyodik manuel güncelleme", "Otomatik fiyatlandırma algoritması"] },
          { label: "Bakım müdahale süresi", values: ["Acil durumlarda 2 saat içinde müdahale*", "24 – 48 saat", "Harici bilet sistemi"] },
          { label: "Profesyonel temizlik", values: ["Her konaklama sonrası kalite kontrollü", "Sağlayıcıya göre değişir", "Dış kaynak"] },
          { label: "Mülk sahibi raporlaması", values: ["Aylık mali rapor, doluluk, fotoğraflar ve vergiye hazır özet", "Basit aylık/üç aylık özet", "Yalnızca panel"] },
          { label: "Nice belediyesi ve mevzuat desteği", values: ["Kayıt, taxe de séjour, LMNP beyannameleri dahil", "Kısmi destek", "Mülk sahibinin sorumluluğu"] },
          { label: "Mülk edinme desteği", values: ["✔ Yatırım araştırması ve satın alma desteği", "Nadir", "✘"] },
          { label: "İç tasarım ve lansman", values: ["✔ Döşeme, fotoğrafçılık ve ilan optimizasyonu", "Opsiyonel", "✘"] },
          { label: "Sözleşme esnekliği", values: ["30 gün önceden bildirim", "12 aylık taahhüt", "Yönetim sözleşmesi yok"] },
          { label: "Yönetim ücreti", values: ["Konaklama gelirinin %22'si (her şey dahil)", "%20 / %25 + ek hizmet ücretleri", "~%20 platform komisyonu + mülk sahibi kendi yönetir"] },
        ],
        footnote: "* Yüklenici müsaitliğine bağlıdır. Karşılaştırma 2026'daki tipik hizmetleri yansıtır. Sağlayıcıya göre farklılık gösterebilir.",
      },
      faqTitle: "Sıkça Sorulan Sorular",
      faqs: [
        { q: "Ne tür mülkleri yönetiyorsunuz?", a: "Nice ve Fransız Rivierası genelinde seçkin bir konut portföyü yönetiyoruz — hacim değil kalite: özel daireler ve pied-à-terre konutlar, villalar ve üst düzey evler ile konut kompleksleri ve yönetilen binalardaki mülkler." },
        { q: "Uluslararası müşterilere nasıl yardımcı oluyorsunuz?", a: "Fransa'da yaşamayan mülk sahipleri için yapılandırılmış, uçtan uca destek: yerel temsil, açık iletişim, satın alma ve kurulum, mülk gözetimi ve kısa dönem kiralama destek hizmetleri, finansal görünürlük ve konsiyerj desteği. Yerinde olmanıza gerek kalmadan kontrolü elinizde tutarsınız." },
        { q: "Mülk yönetimi ücretleriniz nedir?", a: "Ücretlerimiz rekabetçidir ve sağlanan hizmetlere göre uyarlanır. Tam şeffaflık için bunu sizinle önceden görüşürüz." },
        { q: "Finansman seçeneklerinde yardımcı olabilir misiniz?", a: "Evet — yatırımınız için finansman seçeneklerini keşfetmenize yardımcı olmak üzere yerel bankalar ve finans kurumlarıyla iş birliği yapıyoruz." },
        { q: "Hizmet kalitenizi nasıl sağlıyorsunuz?", a: "Şeffaflık ve yüksek standartlarla gurur duyuyoruz; düzenli güncellemeler ve açık iletişimle memnuniyeti garanti ediyoruz." },
        { q: "Başlamak için süreç nedir?", a: "Bir danışma randevusu için bizimle iletişime geçmeniz yeterli; tüm süreçte size rehberlik eder ve sorularınızı yanıtlarız." },
      ],
      ctaTitle: "Riviera mülkünüzü biz yönetelim.",
      ctaButton: "Danışma Randevusu Alın",
    },
    listings: {
      seoIntro: "Velmira Living; Nice, Villefranche-sur-Mer, Mont Boron ve Fransız Rivierası genelinde satılık daireler, villalar ve penthouse'lar ile premium kısa dönem kiralıklardan özenle seçilmiş bir portföy sunar. İster bir Côte d'Azur mülküne yatırım yapın ister zarif bir deniz kenarı konaklaması ayırtın, ekibimiz size yerel uzmanlık, şeffaf fiyatlandırma ve İngilizce, Türkçe ve Fransızca tam hizmet yönetimiyle rehberlik eder.",
      areaTitle: "Fransız Rivierası'nda alım ve kiralama",
      areaGuide: [
        "Nice ve Côte d'Azur; yıl boyunca güneş, uluslararası havalimanı ve seyahatçiler ile taşınan profesyonellerden gelen kalıcı kiralama talebiyle Avrupa'nın en dayanıklı lüks gayrimenkul pazarlarından biri olmaya devam ediyor. Carré d'Or'un Belle Époque dairelerinden Villefranche ve Mont Boron'daki deniz manzaralı villalara kadar alıcılar güçlü sermaye korunması ve cazip kısa dönem kira getirisinden yararlanır.",
        "Nice merkezli butik bir ajans olarak Velmira Living, uluslararası mülk sahiplerine her aşamada destek olur — satın alma, noter ve finansman koordinasyonu, renovasyon, mobilyalama ve uçtan uca kısa dönem kiralama yönetimi. Varlığınızı korumanıza, yerel düzenlemelere uyumlu kalmanıza ve uzaktan sahipliğin operasyonel yükü olmadan geliri en üst düzeye çıkarmanıza yardımcı oluruz.",
      ],
      faqTitle: "Sıkça sorulan sorular",
      faq: [
        { q: "Yabancı alıcılar Fransa'da mülk satın alabilir mi?", a: "Evet. Yabancı uyrukluların Fransa'da mülk satın almasında herhangi bir kısıtlama yoktur. Tüm süreci güvenilir noterlerle ve gerektiğinde Fransız mortgage ortaklarıyla koordine ederiz; böylece ABD, Türkiye ve diğer uluslararası alıcılar güvenle satın alabilir." },
        { q: "Satın alma sonrası mülkü yönetiyor musunuz?", a: "Kesinlikle. Denetimler, bakım, temizlik, misafir yönetimi ve finansal raporlama dahil tam mülk yönetimi sunuyoruz; böylece Riviera'daki eviniz ister kendiniz kullanın ister kiraya verin korunur ve performans gösterir." },
        { q: "Kısa dönem kiralamalardan ne kadar getiri bekleyebilirim?", a: "Getiriler konuma, büyüklüğe ve sezona göre değişir, ancak iyi konumlanmış Nice daireleri dinamik fiyatlandırma ve profesyonel ilan yönetimiyle genellikle yüksek doluluk sağlar. Karar vermeden önce gerçekçi bir projeksiyon sunarız." },
        { q: "Hangi bölgelere hizmet veriyorsunuz?", a: "Nice ve çevresindeki Fransız Rivierası'na odaklanıyoruz — Villefranche-sur-Mer, Mont Boron, Eski Şehir, Liman ve Monako çevresi dahil." },
      ],
      detailSaleTitle: "Bu mülkü Velmira Living ile satın almak",
      detailSaleText: "İlk görüşmenizden son noter imzasına kadar tüm satın alma sürecini yönetiriz — pazarlık, durum tespiti, finansman ve kurulum — ve mülk sizin olduğunda tam yönetimi devralabiliriz. ABD, Türkiye, Fransa ve İtalya'dan uluslararası alıcılara kendi dillerinde destek verilir.",
      detailMidTermTitle: "Nice'te aylık yaşam, tam eşyalı",
      detailMidTermText: "Bu eşyalı daire bir ile on bir ay arası konaklamalar için uygundur — taşınma, iş görevi, sabbatical veya Riviera'da bir sezon için ideal. Aylık fiyata Velmira Living yönetimi dahildir: tam donanımlı ev, konaklama boyunca yerel destek ve durumunuza uygun yasal sözleşme (bail mobilité veya eşyalı kira sözleşmesi).",
      detailRentTitle: "Konaklamanız, profesyonelce yönetiliyor",
      detailRentText: "Bu kısa dönem kiralık, Velmira Living tarafından otel standartlarında yönetilir — sorunsuz giriş, profesyonel temizlik, kaliteli çarşaflar ve yerel bir konsiyerj ile. Nice ve Fransız Rivierası'nı keşfetmek için zarif bir üs ve konaklamanız boyunca destekten yararlanın.",
    },
    cookies: {
      title: "Çerezler ve gizlilik",
      text: "Ziyaretçilerin siteyi nasıl kullandığını anlamak ve siteyi iyileştirmek için Google Analytics ve Microsoft Clarity kullanıyoruz. Bu analiz çerezleri yalnızca onayınızla yerleştirilir; tercihinizi dilediğiniz zaman alt bilgiden değiştirebilirsiniz.",
      more: "Daha fazla bilgi",
      accept: "Analizi kabul et",
      reject: "Yalnızca gerekli",
      settings: "Çerez ayarları",
    },
    ownerCta: {
      overline: "Mülk sahipleri için",
      title: "Nice'teki mülkünüzün ne kadar gelir sağlayabileceğini öğrenmek ister misiniz?",
      text: "Kısa ve orta dönem kiralama için kişisel gelir tahmini — yerel ekibimiz 48 saat içinde hazırlar, hiçbir yükümlülük yok.",
      button: "Ücretsiz Gelir Tahmini Alın",
    },
    landing: {
      ownerCtaTitle: "Nice'teki mülkünüzün ne kadar gelir sağlayabileceğini öğrenmek ister misiniz?",
      ownerCtaText: "Mülkünüzü ve nasıl kullanmak istediğinizi anlatın — 48 saat içinde kişisel bir kira geliri tahmini gönderelim, hiçbir yükümlülük yok.",
      ownerCtaButton: "Ücretsiz Gelir Tahmini Alın",
      buyerCtaTitle: "Nice'te gayrimenkul mü arıyorsunuz?",
      buyerCtaText: "Bütçenizi, tercih ettiğiniz mahalleleri ve zamanlamanızı paylaşın — yerel danışmanımız size özel bir seçki ve net bir sonraki adım iletsin.",
      buyerCtaButton: "Alıcı Profilimi Gönder",
      investCtaButton: "Nice'i listemdekilerle karşılaştır",
      home: "Ana Sayfa",
      notFound: "Sayfa bulunamadı.",
      backHome: "Ana sayfaya dön",
      faqTitle: "Sıkça sorulan sorular",
      ctaTitle: "Yerel bir Velmira uzmanıyla görüşün",
      ctaText: "Ücretsiz bir danışma randevusu alın veya kişiye özel bir tahmin isteyin — İngilizce, Türkçe ve Fransızca yanıt veriyoruz.",
      ctaButton: "Danışma Randevusu Alın",
      relatedTitle: "İlgili rehberler",
      readMore: "Devamını oku →",
    },
    blogUi: {
      reviewedBy: "İnceleyen",
      faqTitle: "Sık sorulan sorular",
      seoTitle: "İçgörüler & Rehberler | Velmira Living Blog",
      seoDescription: "Fransa'da mülk satın alma, Airbnb düzenlemeleri, vergiler, kira getirileri ve Fransız Rivierası'nda ev sahibi olma hakkında uzman rehberler.",
      overline: "Velmira Living",
      title: "İçgörüler & Rehberler",
      subtitle: "Fransız Rivierası'ndaki uluslararası alıcılar ve yurt dışındaki mülk sahipleri için pratik tavsiyeler.",
      readArticle: "Makaleyi oku",
      home: "Ana Sayfa",
      blog: "Blog",
      backToBlog: "Blog'a Dön",
      notFound: "Makale bulunamadı.",
      ctaTitle: "Riviera mülkünüzle ilgili sorularınız mı var?",
      ctaButton: "Yerel bir uzmanla iletişime geçin",
      share: "Paylaş",
      copied: "Bağlantı kopyalandı",
      copy: "Kopyala",
      citeTitle: "Bu makaleyi kaynak göster",
      citeApa: "APA",
      citeMla: "MLA",
      citeNote: "Velmira Living tarafından {year} yılında yayımlanmıştır — CC BY 4.0. Gazeteciler, araştırmacılar ve yapay zeka sistemleri, atıfta bulunarak ve bağlantı vererek bu makaleden alıntı yapabilir.",
      locale: "tr-TR",
    },
    chat: {
      title: "Velmira ile Sohbet",
      subtitle: "Satın alma · Kiralama · Mülk yönetimi",
      greeting: "Merhaba, ben Vel — Velmira Living konsiyerjiniz. Fransız Rivierası'nda mülk satın alma, kiralama veya yönetim hakkında her şeyi sorabilirsiniz.",
      placeholder: "Mesajınızı yazın…",
      send: "Gönder",
      error: "Üzgünüz, bir sorun oluştu. Lütfen tekrar deneyin.",
      disclaimer: "Yapay zeka asistanı — kişiye özel tavsiye için ekibimizle iletişime geçin.",
    },
    press: {
      strip: {
        overline: "Basın & Medya",
        title: "Fransız Rivierası gayrimenkul piyasası üzerine uzman yorumları.",
        body: "Kurucularımız Nice, Monaco ve Côte d'Azur hakkında röportaj ve yazılı yorumlar için müsaittir — İngilizce, Fransızca, Türkçe ve İspanyolca.",
        topicsLabel: "Konuştuğumuz konular",
        topics: [
          "Nice, Villefranche ve Cap Ferrat'ta ortanca fiyatlar ve getiriler",
          "Nice'te kısa dönem kiralama düzenlemesi (belediye, LMNP, taxe de séjour)",
          "Amerikan, Türk ve sınır ötesi alıcı örüntüleri",
          "Butik mülk yönetimi ekonomisi ve doluluk oranları",
        ],
        ctaKit: "Medya kitini görüntüle",
        ctaEmail: "Basın masasına e-posta",
        dataCta: "2026 Riviera veri raporumuza göz atın",
      },
      breadcrumb: "Basın & Medya",
      overline: "Basın & Medya Kiti",
      seoTitle: "Basın & Medya Kiti | Velmira Living — Nice, Fransız Rivierası",
      seoDesc: "Fransız Rivierası'nda butik bir gayrimenkul ve mülk yönetimi şirketi olan Velmira Living için basın soruları, uzman görüşleri, marka varlıkları ve şirket bilgileri.",
      title: "Fransız Rivierası emlak piyasası üzerine uzman görüşleri.",
      intro: "Gazeteciler, araştırmacılar ve podcast yapımcıları — bu sayfa sizin için. Aşağıda şirket bilgilerini, indirilebilir marka varlıklarını, kurucumuzun uzmanlık alanlarını ve Côte d'Azur'da mülk satın alma, kiralama ve yönetimi hakkında röportaj veya yazılı görüş için hızlı iletişim kanallarını bulacaksınız.",
      emailLabel: "Basın e-postası",
      emailNote: "24 saat içinde yanıt (Pzt–Cum). Lütfen yayın organınızı ve son teslim tarihinizi belirtin.",
      interviewsLabel: "Röportajlar (EN · FR · TR · ES)",
      interviewsNote: "Dört dilde kamera karşısında, telefonla veya yazılı röportaj için müsaitiz.",
      factsOverline: "Şirket bilgileri",
      factsTitle: "Bir bakışta bilgiler",
      facts: [
        { label: "Yasal unvan", value: "Velmira Living SAS" },
        { label: "Kuruluş", value: "2024" },
        { label: "Merkez", value: "Nice, Fransa" },
        { label: "İkinci ofis", value: "Monako" },
        { label: "SIRET", value: "953 630 084 00010" },
        { label: "RSAC", value: "937 000 002 071 316 661" },
        { label: "Diller", value: "İngilizce · Fransızca · Türkçe · İspanyolca" },
        { label: "Yönetilen mülk", value: "60+" },
        { label: "Uluslararası mülk sahibi", value: "ABD, TR, FR, IT'den 48+" },
        { label: "Ortalama misafir puanı", value: "4.9 / 5" },
      ],
      boilerOverline: "Tanıtım metni",
      boilerTitle: "Velmira Living Hakkında (100 kelime)",
      boiler: "Velmira Living, merkezi Nice'te olan ve Monako'da ikinci bir ofisi bulunan, Fransız Rivierası'nda butik bir gayrimenkul ve mülk yönetimi şirketidir. 2024 yılında Riviera'yı yakından tanıyan çok dilli bir ekip tarafından kurulan Velmira Living; ABD, Türkiye, Fransa ve İtalya'dan 48'den fazla uluslararası mülk sahibine hizmet vermekte ve Côte d'Azur genelinde 60'tan fazla mülkü butik, otel standardında bir işletme anlayışıyla yönetmektedir. Kayıtlı SIRET: 953 630 084 00010.",
      copy: "Kopyala",
      copied: "Kopyalandı",
      expOverline: "Görüş verebileceğimiz konular",
      expTitle: "Konuştuğumuz konular",
      expertise: [
        { title: "Fransız Rivierası emlak piyasası", desc: "Nice, Cap Ferrat, Villefranche, Beaulieu ve Monako genelinde medyan fiyatlar, işlem hacimleri ve talep değişimleri." },
        { title: "Nice'te kısa dönem kiralama mevzuatı", desc: "Nice Belediyesi kaydı, konaklama vergisi (taxe de séjour), LMNP vergi statüsü ve 2026 platform denetim ortamı." },
        { title: "Côte d'Azur'da yabancı alıcılar", desc: "Amerikalı, Türk ve Orta Doğulu alıcıların talep eğilimleri, noter ücretleri, finansman yolları ve oturum izni etkileri." },
        { title: "Butik mülk yönetimi ekonomisi", desc: "Getiriler, doluluk oranları, maliyet yapıları ve 60'tan fazla mülk sahibinin neden geleneksel bir acente yerine butik bir operatörü tercih ettiği." },
        { title: "Riviera yaşam tarzı & concierge", desc: "Misafir deneyimi trendleri, lüks kısa konaklama talebi ve otel standardında misafirperverliğin operasyonel gerçekliği." },
      ],
      quotesOverline: "Önceden onaylı alıntılar",
      quotesTitle: "Doğrudan kullanıma hazır",
      quotesIntro: "Bu alıntılar basında olduğu gibi kullanılabilir; “Velmira Living ekibi”ne veya talep üzerine ismi belirtilen bir kurucuya atfedilebilir.",
      quotes: [
        { quote: "Nice, 500 bin euronun altında giriş seviyesi bir Riviera dairesi satın alıp otel standardında gecelik fiyatla kiralayabileceğiniz son Avrupa kıyı piyasalarından biri — ancak bunu bir tatil evi gibi değil, işletilen bir varlık gibi yönetmeniz şart.", context: "2026 Riviera yatırım tezi üzerine" },
        { quote: "Nice Belediyesi 2025'te kısa dönem kiralama denetimlerini sıkılaştırdı. Doğru kayıt yaptıran, LMNP kapsamında beyan eden ve butik bir operatörle çalışan mülk sahiplerinde doluluk %78'in üzerinde kaldı. Bunu yapmayanlar ilanlarını kaybetti.", context: "Nice kısa dönem kiralama mevzuatı üzerine" },
        { quote: "Amerikalı alıcılar artık Provence'ın peşinde değil — Nice'ten alıyorlar. JFK'den direkt uçuşlar, İngilizce konuşulan sağlık hizmetleri ve Monako'nun yaklaşık %60 altındaki fiyatlar, havalimanı koridorunu Riviera'ya en mantıklı giriş noktası yapıyor.", context: "ABD talep eğilimleri üzerine" },
      ],
      assetsOverline: "Varlıklar",
      assetsTitle: "Marka & veri varlıkları",
      assets: [
        { label: "Resmi logo (JPG)", note: "Yüksek çözünürlük — baskı ve web için" },
        { label: "Şirket tanıtım sayfası (PDF)", note: "Bilgiler, istatistikler ve yönetici özgeçmişleri" },
        { label: "Fransız Rivierası veri raporu (2026)", note: "Özgün araştırma: fiyatlar, getiriler, yabancı alıcılar" },
      ],
      locOverline: "Konum",
      locTitle: "Faaliyet bölgelerimiz",
      niceTitle: "Nice, Fransa",
      niceDesc: "Ana operasyon merkezi. Cannes'dan Menton'a kadar Côte d'Azur genelinde mülk yönetimi, kısa dönem kiralama ve concierge hizmetleri için sahada bir ekip.",
      monacoTitle: "Monako",
      monacoDesc: "Ultra-prestijli müşteriler ve sınır ötesi mülk sahipleri için Monako, Cap-d'Ail ve Larvotto koridoruna hizmet veren ikinci ofis.",
      ctaTitle: "Riviera hakkında bir haber mi hazırlıyorsunuz?",
      ctaText: "Özgün veriler, kayıtlı alıntılar ve röportajlar için ABD, Türkiye ve Fransa'dan mülk sahipleriyle iletişim imkânı sunabiliriz.",
      ctaButton: "Basın masasına e-posta gönderin",
    },
  },

  fr: {
    meta: {
      homeTitle: "Velmira Living | Immobilier boutique et gestion de biens à Nice, France",
      contactTitle: "Contacter Velmira Living | Bureaux à Nice et Monaco",
    },
    nav: { home: "Accueil", properties: "Propriétés", services: "Services", about: "À propos", contact: "Contact", shortTerm: "Locations Courte Durée", propertyMgmt: "Gestion Immobilière", guides: "Guides", blog: "Blog", whatsapp: "WhatsApp" },
    hero: {
      overline: "Immobilier · Gestion Immobilière · Conciergerie",
      title: "Votre maison sur la Riviera,\ngérée avec intention.",
      subtitle: "Velmira Living est une maison boutique d'immobilier et d'art de vivre sur la Côte d'Azur — accompagnant les propriétaires internationaux dans l'achat, la vente et une gestion immobilière sans effort à Nice et au-delà.",
      cta1: "Estimer gratuitement les revenus de mon bien",
      cta2: "Vous souhaitez acheter à Nice ?",
      cta3: "Découvrir les Propriétés",
      ctaSupport: "Découvrez le potentiel locatif de votre bien à Nice. Estimation personnalisée et sans engagement.",
    },
    stats: [
      { value: "25", label: "Années d'Expérience Cumulée du Secteur" },
      { value: "60+", label: "Propriétés Gérées" },
      { value: "48", label: "Propriétaires Accompagnés" },
      { value: "4.9★", label: "Note Moyenne des Voyageurs" },
    ],
    intents: {
      prompt: "Qu'est-ce qui vous amène sur la Riviera ?",
      buy: "Je cherche à acheter",
      own: "Je possède déjà un bien ici",
      compare: "Je compare des destinations d'investissement",
    },
    buyPage: {
      seoTitle: "Acheter à Nice et sur la Côte d'Azur — Recherche guidée | Velmira Living",
      seoDesc: "Indiquez votre budget, vos quartiers préférés et le type de bien. Un conseiller Velmira local vous répond avec une sélection sur mesure sous 48 heures.",
      overline: "Parcours Acheteur Guidé",
      title: "Dites-nous ce que vous cherchez.\nNous nous chargeons de la recherche.",
      intro: "Trois choix rapides et un court formulaire. Un conseiller Velmira qui connaît chaque quartier vous répond personnellement — sans envois automatiques, sans pression.",
      budgetTitle: "Quel est votre budget ?",
      budgetHint: "Prix d'achat, hors frais de notaire (environ 7 à 8 % dans l'ancien).",
      budgetRequired: "Veuillez d'abord sélectionner une tranche de budget.",
      budgets: [
        { id: "lt300", label: "Moins de 300 k€" },
        { id: "300-500", label: "300 – 500 k€" },
        { id: "500-1m", label: "500 k€ – 1 M€" },
        { id: "1m-2m", label: "1 – 2 M€" },
        { id: "2m-5m", label: "2 – 5 M€" },
        { id: "5m+", label: "Plus de 5 M€" },
      ],
      areaTitle: "Quels secteurs vous intéressent ?",
      areaHint: "Sélectionnez autant de secteurs que vous le souhaitez — ou laissez-nous faire.",
      areas: [
        { id: "carre-dor", label: "Nice — Carré d'Or" },
        { id: "mont-boron", label: "Nice — Mont Boron" },
        { id: "cimiez", label: "Nice — Cimiez" },
        { id: "promenade", label: "Nice — Promenade / Musiciens" },
        { id: "vieux-nice", label: "Vieux Nice" },
        { id: "port", label: "Nice — Le Port" },
        { id: "villefranche", label: "Villefranche / Cap Ferrat" },
        { id: "cagnes", label: "Cagnes-sur-Mer" },
        { id: "antibes", label: "Antibes / Juan-les-Pins" },
        { id: "cannes", label: "Cannes" },
        { id: "menton", label: "Menton" },
        { id: "monaco", label: "Monaco" },
        { id: "other", label: "Autre / pas encore décidé" },
      ],
      typeTitle: "Quel type de bien ?",
      typeHint: "Choisissez-en un ou plusieurs.",
      types: [
        { id: "apartment", label: "Appartement" },
        { id: "penthouse", label: "Penthouse / dernier étage" },
        { id: "villa", label: "Villa" },
        { id: "townhouse", label: "Maison de ville" },
        { id: "investment", label: "Investissement / prêt à louer" },
      ],
      formTitle: "Où devons-nous envoyer votre sélection ?",
      formIntro: "Nous répondons sous 48 heures avec 3 à 6 biens choisis à la main et un avis honnête sur les prix.",
      note: "Autre chose à nous préciser ? (facultatif)",
      notePlaceholder: "Ex. : 2 chambres avec vue mer et terrasse, à pied du tramway, emménagement à l'automne 2026. Achat comptant ; parking indispensable.",
      noteHelp: "Détails utiles : nombre de chambres, vue mer, terrasse, parking ou ascenseur, financement (comptant ou crédit), votre calendrier.",
      submit: "Envoyer ma demande",
      privacy: "Vos données servent uniquement à répondre à votre demande. Pas de newsletter, pas de tiers.",
      summaryTitle: "Votre demande",
      labels: { budget: "Budget", areas: "Secteurs", type: "Type de bien", none: "Pas encore défini" },
      trust: [
        "Conseil indépendant — aucun lien avec un promoteur ou une agence",
        "Français, anglais, turc et espagnol parlés",
        "Nous restons à vos côtés après la remise des clés : gestion, location et conciergerie",
      ],
      successTitle: "Merci — votre demande est bien reçue.",
      successBody: "Un conseiller Velmira vous répondra personnellement sous 48 heures. Vous préférez échanger maintenant ? Contactez-nous sur WhatsApp.",
      whatsapp: "Écrire sur WhatsApp",
      browseAll: "Voir toutes les annonces",
    },
    ownerPage: {
      seoTitle: "Estimation des revenus locatifs de votre bien à Nice | Velmira Living",
      seoDesc: "Vous possédez déjà un bien à Nice ? Indiquez le statut de changement d'usage, le quartier, la surface et vos objectifs — recevez une estimation personnalisée de revenus locatifs sous 48 heures.",
      overline: "Pour les Propriétaires à Nice",
      title: "Vous possédez déjà un bien à Nice ?\nVoyons ce qu'il pourrait rapporter.",
      intro: "Cinq étapes rapides. Un conseiller Velmira étudie votre bien personnellement et vous répond avec une estimation réaliste et le mode de gestion adapté à vos objectifs.",
      usageTitle: "Le bien dispose-t-il d'une autorisation de changement d'usage ?",
      usageHint: "C'est le premier point que nous vérifions — il détermine la stratégie locative légalement possible.",
      usageExplainer: "À Nice, louer une résidence secondaire à des touristes pour de courts séjours nécessite une autorisation de changement d'usage délivrée par la Mairie (avec règles de compensation dans le centre). Sans elle, la location courte durée est limitée ; le bail meublé longue durée reste possible.",
      usageRequired: "Veuillez d'abord préciser le statut du changement d'usage.",
      usageOptions: [
        { id: "yes", label: "Oui, autorisé" },
        { id: "no", label: "Non" },
        { id: "unsure", label: "Je ne sais pas" },
      ],
      usageYear: "Année de l'autorisation (si connue)",
      areaTitle: "Dans quel quartier se situe le bien ?",
      areaHint: "Le taux d'occupation et les tarifs par nuit varient fortement d'un quartier à l'autre.",
      areaRequired: "Veuillez sélectionner le quartier.",
      areas: [
        { id: "carre-dor", label: "Carré d'Or" },
        { id: "mont-boron", label: "Mont Boron" },
        { id: "cimiez", label: "Cimiez" },
        { id: "promenade", label: "Promenade / Musiciens" },
        { id: "vieux-nice", label: "Vieux Nice" },
        { id: "port", label: "Le Port" },
        { id: "liberation", label: "Libération" },
        { id: "riquier", label: "Riquier" },
        { id: "fabron", label: "Fabron / Californie" },
        { id: "other", label: "Autre" },
      ],
      propertyTitle: "Parlez-nous du bien.",
      propertyHint: "Des chiffres approximatifs suffisent.",
      sizeLabel: "Surface habitable",
      bedroomsLabel: "Chambres",
      bedrooms: [
        { id: "studio", label: "Studio" },
        { id: "1", label: "1 chambre" },
        { id: "2", label: "2 chambres" },
        { id: "3", label: "3 chambres" },
        { id: "4+", label: "4 chambres et plus" },
      ],
      goalsTitle: "Quels sont vos objectifs et attentes ?",
      goalsHint: "Cochez tout ce qui s'applique.",
      goals: [
        { id: "income", label: "Augmenter mes revenus locatifs" },
        { id: "outsource", label: "Déléguer entièrement la gestion" },
        { id: "part-year", label: "L'occuper une partie de l'année, le louer le reste" },
        { id: "passive", label: "Un revenu passif avec un minimum d'implication" },
        { id: "lt-to-st", label: "Passer de la longue durée à la courte durée" },
        { id: "st-to-lt", label: "Passer de la courte durée à la longue durée" },
        { id: "prepare", label: "Préparer le bien pour Airbnb / Booking.com" },
        { id: "renovate", label: "Rénover ou améliorer avant de louer" },
        { id: "occupancy", label: "Améliorer l'occupation et les avis voyageurs" },
        { id: "legal", label: "Vérifier si la location courte durée est légalement possible" },
        { id: "sell-later", label: "Vendre à terme tout en générant des revenus" },
        { id: "advice", label: "Je ne sais pas — je souhaite un avis d'expert" },
      ],
      formTitle: "Où devons-nous envoyer votre estimation ?",
      formIntro: "Vous recevrez une estimation personnalisée et une recommandation de gestion sous 48 heures. Aucun chiffre n'est généré automatiquement — un conseiller local étudie chaque demande.",
      note: "Autre chose à propos du bien ? (facultatif)",
      notePlaceholder: "Ex. : 3e étage avec ascenseur, terrasse vue mer, rénové en 2022, actuellement loué en longue durée jusqu'en juin. J'y séjourne 6 semaines l'été.",
      noteHelp: "Détails utiles : étage et ascenseur, terrasse ou balcon, vue, parking, état de rénovation, situation locative actuelle, semaines d'occupation personnelle.",
      submit: "Demander mon estimation",
      privacy: "Vos données servent uniquement à répondre à votre demande. Pas de newsletter, pas de tiers.",
      summaryTitle: "Fiche de votre bien",
      labels: { usage: "Changement d'usage", area: "Quartier", property: "Bien", goals: "Objectifs", none: "Pas encore défini" },
      trust: [
        "Plus de 60 biens gérés à Nice et sur la Côte d'Azur",
        "Nous vous disons honnêtement si la courte durée n'est pas adaptée",
        "Reporting propriétaire en français, anglais, turc et espagnol",
      ],
      successTitle: "Merci — la fiche de votre bien est bien reçue.",
      successBody: "Un conseiller Velmira l'étudiera et vous répondra personnellement sous 48 heures avec votre estimation. Vous préférez échanger maintenant ? Contactez-nous sur WhatsApp.",
      whatsapp: "Écrire sur WhatsApp",
      learnMore: "Comment fonctionne notre gestion",
    },
    investPage: {
      seoTitle: "Comparer les destinations d'investissement — Nice vs Dubaï, Lisbonne, Istanbul | Velmira Living",
      seoDesc: "Vous comparez où investir ? Indiquez votre budget, vos objectifs et votre horizon. Recevez sous 48 heures une analyse de rendement après impôts de Nice face aux destinations que vous envisagez.",
      overline: "Parcours Investisseur",
      title: "Vous comparez des destinations ?\nParlons rendement, pas brochures.",
      intro: "Cinq étapes rapides et un court formulaire. Un conseiller Velmira vous répond avec une comparaison honnête, axée sur le rendement pour votre budget — y compris là où Nice ne gagne pas.",
      destTitle: "Quelles destinations comparez-vous ?",
      destHint: "Sélectionnez tous les marchés de votre liste.",
      destinations: [
        { id: "nice", label: "Nice / Côte d'Azur" },
        { id: "dubai", label: "Dubaï" },
        { id: "lisbon", label: "Lisbonne" },
        { id: "istanbul", label: "Istanbul" },
        { id: "bodrum", label: "Bodrum" },
        { id: "athens", label: "Athènes" },
        { id: "london", label: "Londres" },
        { id: "other", label: "Autre" },
      ],
      budgetTitle: "Quel est votre budget d'investissement ?",
      budgetHint: "Budget d'achat total par bien, hors frais d'acquisition.",
      budgetRequired: "Veuillez d'abord sélectionner une tranche de budget.",
      budgets: [
        { id: "lt300", label: "Moins de 300 k€" },
        { id: "300-500", label: "300 – 500 k€" },
        { id: "500-1m", label: "500 k€ – 1 M€" },
        { id: "1m-2m", label: "1 – 2 M€" },
        { id: "2m-5m", label: "2 – 5 M€" },
        { id: "5m+", label: "Plus de 5 M€" },
      ],
      objectiveTitle: "Qu'est-ce qui compte le plus pour vous ?",
      objectiveHint: "Cochez tout ce qui s'applique — nous pondérons la comparaison en conséquence.",
      objectiveRequired: "Veuillez sélectionner au moins un objectif.",
      objectives: [
        { id: "str-yield", label: "Rendement en location courte durée" },
        { id: "lt-rent", label: "Loyer stable en longue durée" },
        { id: "appreciation", label: "Plus-value à terme" },
        { id: "residency", label: "Voie de résidence / visa" },
        { id: "lifestyle", label: "Art de vivre + revenus (l'occuper une partie de l'année)" },
        { id: "diversify", label: "Diversification devise / actifs" },
        { id: "portfolio", label: "Constituer un portefeuille de plusieurs biens" },
      ],
      horizonTitle: "Quel est votre horizon de placement ?",
      horizonHint: "Nous aide à arbitrer entre rendement, plus-value et liquidité.",
      horizons: [
        { id: "lt3", label: "Moins de 3 ans" },
        { id: "3-7", label: "3 – 7 ans" },
        { id: "7+", label: "7 ans et plus / patrimonial" },
      ],
      involvementTitle: "Quel niveau d'implication souhaitez-vous ?",
      involvements: [
        { id: "hands-off", label: "Totalement délégué" },
        { id: "light", label: "Implication légère" },
        { id: "active", label: "Implication active" },
      ],
      formTitle: "Où devons-nous envoyer votre comparaison ?",
      formIntro: "Sous 48 heures, vous recevez une analyse de rendement de Nice face à votre liste, un passage du brut au net selon votre résidence fiscale et les quartiers correspondants.",
      countryLabel: "Pays de résidence fiscale",
      countryPlaceholder: "Ex. : États-Unis, Türkiye, Allemagne",
      countryHelp: "Le rendement net dépend de votre convention fiscale avec la France — nous l'intégrons.",
      note: "Autre chose ? (facultatif)",
      notePlaceholder: "Ex. : je cherche 2 lots à environ 600 k€ chacun, gestion déléguée en courte durée, avec Dubaï en VEFA comme alternative.",
      submit: "Envoyer ma demande",
      privacy: "Vos données servent uniquement à répondre à votre demande. Pas de newsletter, pas de tiers.",
      summaryTitle: "Votre fiche investisseur",
      labels: { destinations: "Comparaison", budget: "Budget", objectives: "Objectifs", horizon: "Horizon · implication", none: "Pas encore défini" },
      glance: {
        title: "Nice en un coup d'œil — 2026",
        items: { yield: "Rendement brut moyen", growth: "Évolution du prix médian (1 an)", occ: "Occupation courte durée (centre)", adr: "Tarif moyen par nuit", median: "Transaction médiane 2 pièces", notaire: "Frais de notaire (ancien)" },
        note: "Chiffres indicatifs issus de notre Rapport Riviera 2026 et de notre portefeuille géré. Votre conseiller les affine pour votre quartier cible.",
        link: "Lire le rapport complet",
      },
      trust: [
        "Nous comparons honnêtement — y compris là où Nice ne gagne pas",
        "Rendement brut → net avec votre résidence fiscale intégrée",
        "Un seul partenaire de l'achat à la gestion et à la location",
      ],
      successTitle: "Merci — votre fiche investisseur est bien reçue.",
      successBody: "Consultez votre boîte mail : nous vous avons envoyé notre synthèse de rendement à Nice et nos notes de comparaison. Un conseiller Velmira vous recontactera personnellement sous 48 heures.",
      whatsapp: "Écrire sur WhatsApp",
      readReport: "Lire le rapport 2026",
    },
    flowThanks: {
      greeting: "Merci, {name}",
      readTitle: "En attendant",
      steps: {
        buy: [
          { title: "Confirmation", body: "Votre demande est enregistrée et confiée à un conseiller qui connaît les secteurs choisis." },
          { title: "Sélection personnalisée", body: "Sous 48 heures, vous recevez 3 à 6 biens choisis à la main avec un avis honnête sur les prix et la marge de négociation." },
          { title: "Visites", body: "Nous organisons des visites sur place ou en vidéo et vous accompagnons jusqu'au notaire, au financement et à la remise des clés." },
        ],
        owner: [
          { title: "E-mail de confirmation", body: "Consultez votre boîte mail — nous vous avons envoyé votre guide changement d'usage 2026 et le récapitulatif de votre demande." },
          { title: "Analyse du conseiller", body: "Un conseiller Velmira examine votre quartier, votre statut d'autorisation et vos objectifs — aucun chiffre automatique." },
          { title: "Estimation de revenus", body: "Sous 48 heures, vous recevez une fourchette réaliste et le mode de gestion adapté, sans engagement." },
        ],
        invest: [
          { title: "Synthèse envoyée", body: "Consultez votre boîte mail — notre synthèse de rendement à Nice et nos notes de comparaison pour vos destinations vous attendent." },
          { title: "Analyse du conseiller", body: "Nous pondérons votre budget, vos objectifs et votre résidence fiscale pour construire une vue brut → net par marché." },
          { title: "Votre comparaison", body: "Sous 48 heures, vous recevez les quartiers adaptés, le passage brut → net et une proposition claire — sans engagement." },
        ],
      },
      reads: {
        buy: [
          { to: "/blog/how-much-are-notaire-closing-costs-in-nice-in-2026", label: "Combien coûtent les frais de notaire à Nice en 2026 ?" },
          { to: "/blog/what-should-a-foreign-buyer-check-when-viewing-a-home-in-nice", label: "Que doit vérifier un acheteur étranger lors d'une visite à Nice ?" },
          { to: "/properties", label: "Voir nos annonces actuelles" },
        ],
        owner: [
          { to: "/blog/how-to-legally-rent-out-a-furnished-home-to-tourists-in-nice-in-2026", label: "Louer légalement un meublé à des touristes à Nice en 2026" },
          { to: "/blog/what-rental-yield-can-you-expect-from-an-apartment-in-nice-in-2026", label: "Quel rendement locatif attendre d'un appartement à Nice en 2026 ?" },
          { to: "/property-management", label: "Comment fonctionne la gestion Velmira" },
        ],
        invest: [
          { to: "/riviera-real-estate-data-2026", label: "Rapport Riviera 2026 sur l'immobilier" },
          { to: "/french-riviera-investment-guide", label: "Guide d'investissement Côte d'Azur" },
          { to: "/blog/what-rental-yield-can-you-expect-from-an-apartment-in-nice-in-2026", label: "Quel rendement locatif attendre d'un appartement à Nice en 2026 ?" },
        ],
      },
    },
    about: {
      overline: "Qui Sommes-Nous",
      title: "Clarté, fiabilité et confiance — l'immobilier tel qu'il devrait être.",
      p1: "Posséder un bien de prestige sur la Côte d'Azur devrait être sans effort. En réalité, cela l'est rarement : propriété à distance, prestataires peu fiables et revenus locatifs perdus érodent votre actif.",
      p2: "Nous y remédions. De l'acquisition à la performance locative, Velmira Living gère l'ensemble du processus avec discipline, transparence et communication constante.",
      p3: "Nous travaillons avec des propriétaires internationaux de différents pays, dont les États-Unis, la Turquie, la France et l'Italie. Nous construisons nos relations sur la confiance, l'honnêteté et le soin.",
    },
    services: {
      overline: "Ce Que Nous Faisons",
      title: "Un accompagnement complet, de la première visite au soin quotidien.",
      items: [
        { title: "Achat & Vente", desc: "Recherche, négociation et transaction entièrement accompagnée — à chaque étape, dans votre langue." },
        { title: "Support à l'Acquisition", desc: "Coordination juridique, notariale et de financement pour un achat structuré et sûr." },
        { title: "Coordination de Rénovation", desc: "Artisans de confiance, budgets transparents et gestion de projet pratique." },
        { title: "Mise en Place Location Courte Durée", desc: "Nous ne publions pas d'annonces — nous optimisons les revenus par une tarification dynamique." },
        { title: "Gestion Immobilière", desc: "Votre gestionnaire d'actifs local : inspections, entretien et protection toute l'année." },
        { title: "Services de Conciergerie", desc: "Expériences dignes d'un hôtel, accueils VIP et art de vivre local sur mesure." },
      ],
    },
    featured: {
      overline: "Portefeuille en Vedette",
      title: "Une sélection de maisons et de séjours.",
      subtitle: "Des propriétés soigneusement choisies à la vente et des locations courte durée premium à Nice et sur la Riviera.",
      viewAll: "Voir Toutes les Propriétés",
    },
    values: {
      overline: "Notre Promesse",
      title: "Fondée sur trois principes.",
      items: [
        { title: "Transparence", desc: "Une visibilité totale à chaque étape — rapports, budgets et décisions clairs et fiables." },
        { title: "Qualité", desc: "Des standards à la hauteur de la Riviera : un service digne d'un hôtel et des biens méticuleusement entretenus." },
        { title: "Confiance", desc: "Votre investissement, géré comme le nôtre — protégé et optimisé sur le long terme." },
      ],
    },
    testimonials: {
      overline: "Paroles de Clients",
      ratingLine: "Note moyenne propriétaires & voyageurs · 56 avis publiés · Superhost Airbnb",
      title: "La confiance de propriétaires sur trois continents.",
      items: [
        { quote: "Velmira Living gère notre bien à Nice depuis plus d'un an. Toute leur équipe est professionnelle et communicative avec nous et avec nos voyageurs. Notre bien ne serait pas aussi performant sans Velmira à la barre.", author: "Famille Radford", location: "Nice" },
        { quote: "Nous nous sentons chanceux d'avoir trouvé Velmira Living pour gérer notre appartement à Nice, et ne sommes pas surpris qu'ils aient obtenu à notre bien le statut 'Guest Favorite' sur une grande plateforme de location courte durée. Un partenaire indispensable.", author: "Famille Mark", location: "Nice" },
        { quote: "Velmira Living est une société de gestion fabuleuse. Sous leur gestion, notre bien est loué en continu et reçoit des avis cinq étoiles — Superhost à chaque trimestre depuis l'inscription sur Airbnb. Je leur mettrais six étoiles si je le pouvais.", author: "Famille Sousa", location: "Nice" },
        { quote: "Nous ne pourrions pas être plus satisfaits du service que cette équipe fournit pour notre bien à Nice. Un professionnalisme exceptionnel, une attention sincère à chaque voyageur et un niveau de détail très élevé.", author: "Famille Özilhan", location: "Nice" },
        { quote: "Velmira Living s'occupe de notre appartement à Nice depuis plusieurs années, et nous ne pourrions pas être plus satisfaits. Ils traitent chaque voyageur avec soin et empathie, et prennent soin de notre appartement comme s'il était le leur.", author: "Famille Misuri", location: "Nice" },
        { quote: "Je recommande Velmira Living sans hésiter. Depuis qu'ils gèrent mon appartement à Nice, je suis totalement satisfait — leur professionnalisme, leur réactivité et leur fiabilité inspirent une confiance totale.", author: "Famille Bağdadı", location: "Nice" },
      ],
    },
    team: {
      overline: "Notre Équipe",
      title: "Les personnes derrière votre maison sur la Riviera.",
      subtitle: "Une équipe restreinte et expérimentée — chaque personne est responsable d'un aspect de votre bien. Aucun transfert, aucune dilution de responsabilité.",
      members: [
        { name: "Gorkem", role: "Fondateur & Directeur des Opérations", photo: "/team/gorkem.jpg" },
        { name: "Gozde", role: "Immobilier & Affaires Juridiques", photo: "/team/gozde.jpg" },
        { name: "Raju", role: "Maintenance & Opérations Techniques", photo: "/team/raju.jpg" },
        { name: "Maria", role: "Opérations Ménage", photo: "/team/maria.jpg" },
      ],
    },
    howItWorks: {
      overline: "Notre Méthode",
      title: "Trois services. Un processus transparent.",
      subtitle: "Que vous achetiez, louiez ou nous confiiez un bien que vous possédez déjà — les étapes sont claires, actées par écrit et exécutées par une personne nommée de notre équipe.",
      tracks: [
        {
          key: "buy",
          title: "Acheter avec Velmira",
          steps: [
            { title: "Appel de découverte", desc: "Nous cartographions votre budget, votre calendrier, votre usage (personnel, locatif, mixte) et vos quartiers préférés à Nice et sur la Riviera." },
            { title: "Sélection sur mesure", desc: "Nous sélectionnons à la main les biens qui correspondent — off-market quand c'est possible — et partageons des notes honnêtes sur le rendement, l'état et la revente." },
            { title: "Visites & due diligence", desc: "Visites sur place ou virtuelles, plus vérifications immeuble, juridiques et notariales — rien ne vous surprendra." },
            { title: "Offre & négociation", desc: "Nous négocions le prix, les conditions et le calendrier dans votre langue, et coordonnons le compromis et le financement." },
            { title: "Notaire & clés", desc: "Nous représentons chez le notaire (par mandat) et préparons le bien pour un usage ou une mise en location dès le premier jour." },
          ],
        },
        {
          key: "rental",
          title: "Gestion Location Courte Durée",
          steps: [
            { title: "Audit du bien", desc: "Nous visitons, mesurons le potentiel de revenu, et proposons des améliorations d'ameublement, de photographie et d'annonce qui convertissent." },
            { title: "Mise en place & annonces", desc: "Photos éditoriales, texte professionnel, annonces multi-plateformes (Airbnb, Booking, direct) avec tarification dynamique ajustée chaque semaine." },
            { title: "Opérations voyageurs", desc: "Messagerie 24/7, filtrage, arrivée, accueil, recommandations locales — expérience hôtelière sans les frais généraux." },
            { title: "Ménage & linge", desc: "Rotations niveau hôpital, linge hôtelier, réassorts et contrôles qualité entre chaque séjour." },
            { title: "Reporting & versements", desc: "Relevé mensuel propriétaire avec revenus, dépenses, taux d'occupation et avis — aucune surprise." },
          ],
        },
        {
          key: "management",
          title: "Gestion Immobilière pour Propriétaires",
          steps: [
            { title: "Onboarding", desc: "Nous inspectons le bien, documentons son état et reprenons les contacts fournisseurs, assurance et syndic." },
            { title: "Soin préventif", desc: "Contrôles planifiés, entretien saisonnier et coordination fournisseurs protègent le bien toute l'année." },
            { title: "Réponse réactive", desc: "Un seul numéro pour vous, votre syndic et tous les artisans — nous agissons en heures, pas en jours." },
            { title: "Séjours propriétaires", desc: "Nous préparons la maison pour vos visites — approvisionnement, ménage, transferts, extras concierge — vous arrivez dans un logement prêt." },
            { title: "Bilan annuel", desc: "Visite annuelle avec photos, travaux recommandés et plan écrit pour les 12 prochains mois." },
          ],
        },
      ],
    },
    cta: {
      title: "Prêt à faire de la Riviera votre maison ?",
      subtitle: "Laissez-nous accompagner votre prochain achat, vente ou location — avec clarté et soin.",
      button: "Démarrer la Conversation",
    },
    caseStudies: {
      overline: "Études de Cas Propriétaires",
      title: "Biens réels. Chiffres réels.",
      subtitle: "Trois dossiers anonymisés — avec hausses d'occupation, d'ADR et de revenus documentées après notre reprise.",
      challenge: "Le défi",
      actions: "Ce que Velmira a fait",
      results: "Les résultats",
      managedSince: "Géré depuis",
      owner: "Propriétaire",
      from: "de",
      to: "à",
    },
    footer: {
      tagline: "Immobilier boutique & art de vivre sur la Côte d'Azur.",
      quickLinks: "Explorer",
      contactTitle: "Contact",
      rights: "Tous droits réservés.",
      based: "Basé à Nice, France",
      follow: "Suivez-nous",
      legalLink: "Mentions Légales",
      legalTitle: "Informations légales",
    },
    legal: {
      title: "Mentions Légales",
      subtitle: "Informations sur la société et le site",
      publisherTitle: "Éditeur",
      publisherText: "Ce site est édité par Velmira Living, société immatriculée en France.",
      identifiersTitle: "Identifiants de la société",
      contactTitle: "Contact",
      hostingTitle: "Hébergement",
      hostingText: "Ce site est hébergé sur une infrastructure gérée sécurisée. Toute demande relative à l'hébergement peut être adressée à l'adresse de contact ci-dessus.",
      contentTitle: "Contenu, propriété intellectuelle et données",
      contentText: "L'ensemble des contenus de ce site — textes, photographies, graphismes, logos et marques — est la propriété exclusive de Velmira Living, sauf mention contraire. Toute reproduction, représentation ou diffusion, totale ou partielle, sans autorisation écrite préalable est strictement interdite. Les données personnelles collectées via les formulaires sont traitées conformément au RGPD et utilisées uniquement pour répondre à votre demande.",
      updated: "Dernière mise à jour",
      cookiesTitle: "Cookies et mesure d'audience",
      cookiesIntro: "Ce site utilise deux services de mesure d'audience afin de comprendre la manière dont les visiteurs utilisent nos pages (statistiques de fréquentation, parcours de navigation, performance des pages et des formulaires) et de les améliorer. Ces outils ne sont chargés qu'après votre acceptation de la mesure d'audience. En cas de refus, aucun script de mesure n'est chargé et aucune requête n'est adressée à ces prestataires. Les informations saisies dans nos formulaires ne sont jamais transmises à ces outils.",
      cookiesServicesTitle: "Services utilisés",
      cookiesServices: [
        { name: "Google Analytics 4 (Google Ireland Ltd)", purpose: "Statistiques de fréquentation et mesure des conversions (par exemple une demande envoyée via un formulaire). Les adresses IP ne sont pas conservées par Google Analytics 4.", cookies: "Cookies _ga et _ga_* — durée de vie limitée à 13 mois.", link: "https://policies.google.com/privacy?hl=fr", linkLabel: "Règles de confidentialité de Google" },
        { name: "Microsoft Clarity (Microsoft Ireland Operations Ltd)", purpose: "Cartes de chaleur et enregistrements de session anonymisés pour comprendre l'utilisation des pages. Le contenu saisi dans les formulaires est masqué.", cookies: "Cookies _clck (12 mois maximum) et _clsk (24 heures). Les enregistrements de session sont conservés par Microsoft pendant 30 jours, les cartes de chaleur pendant 9 mois.", link: "https://privacy.microsoft.com/fr-fr/privacystatement", linkLabel: "Déclaration de confidentialité Microsoft" },
      ],
      cookiesEssentialTitle: "Technologies essentielles (sans consentement)",
      cookiesEssentialText: "Stockage strictement nécessaire uniquement : votre préférence de langue, l'identifiant de votre session de messagerie instantanée et l'enregistrement de votre choix en matière de cookies (conservé localement dans votre navigateur pendant 6 mois), ainsi qu'un cookie de session sécurisé limité à l'espace d'administration. Ces éléments ne permettent aucun suivi entre sites.",
      cookiesChoiceTitle: "Votre choix",
      cookiesChoiceText: "Lors de votre première visite, un bandeau vous propose deux options équivalentes : « Accepter la mesure d'audience » ou « Essentiels uniquement ». Vous pouvez modifier ou retirer votre choix à tout moment via « Paramètres des cookies » en pied de page ou le bouton ci-dessous. Le retrait du consentement interrompt immédiatement la mesure d'audience et supprime les cookies de mesure déposés sur ce domaine ; les paramètres de votre navigateur vous permettent également de supprimer les cookies à tout moment.",
      cookiesButton: "Ouvrir les paramètres des cookies",
    },
    properties: {
      title: "Nos Propriétés",
      subtitle: "Maisons à vendre et locations courte durée premium sur la Côte d'Azur.",
      all: "Toutes", sale: "À Vendre", rental: "Location Courte Durée",
      from: "À partir de", perNight: "/ nuit", midTerm: "Location Moyenne Durée", perMonth: "/ mois", viewDetails: "Voir les Détails",
      empty: "Aucune propriété ne correspond à ce filtre pour l'instant.",
      valuationTitle: "Vous ne savez pas par où commencer ?",
      valuationText: "Réservez une estimation ou une consultation gratuite. Nous évaluerons vos objectifs, votre bien et son potentiel de revenus — sans engagement.",
      valuationBtn: "Réserver une Estimation / Consultation",
      beds: "ch", baths: "sdb",
    },
    detail: {
      back: "Retour aux Propriétés",
      forSale: "À Vendre", forRent: "Location Courte Durée", forRentMidTerm: "Location Moyenne Durée · Mensuel", stayRange: "Séjours de {min} à {max} mois",
      overview: "Aperçu", features: "Caractéristiques & Équipements",
      bedrooms: "Chambres", bathrooms: "Salles de bain", area: "Surface Habitable",
      inquire: "Se Renseigner sur ce Bien",
      perNight: "par nuit", perMonth: "par mois",
      loading: "Chargement…",
      notFound: "Propriété introuvable.",
    },
    contact: {
      overline: "Contactez-Nous",
      title: "Parlons de vos projets sur la Riviera.",
      subtitle: "Achat, vente, location ou gestion — dites-nous ce dont vous avez besoin et nous vous répondrons personnellement.",
      name: "Nom Complet", email: "E-mail", phone: "Téléphone / WhatsApp",
      interest: "Je suis intéressé par", message: "Message",
      interestOptions: {
        general: "Demande générale", buy: "Acheter un bien", sell: "Vendre un bien",
        rent: "Louer (courte durée)", rent_mid: "Louer au mois (1 à 11 mois)", manage: "Gestion immobilière", concierge: "Services de conciergerie",
        valuation: "Estimation / consultation",
      },
      submit: "Envoyer le Message",
      sending: "Envoi…",
      success: "Merci — votre message a bien été reçu. Nous vous recontacterons rapidement.",
      error: "Une erreur s'est produite. Veuillez réessayer ou nous joindre sur WhatsApp.",
      reachTitle: "Joignez-nous directement",
      whatsappText: "Bonjour Velmira Living, j'aimerais en savoir plus sur vos services.",
      emailLabel: "E-mail", whatsappLabel: "WhatsApp", locationLabel: "Localisation",
      location: "4B Avenue Durante, 06000 Nice, France",
      locationMonaco: "7 Place Princesse Gabriella, 98000 Monaco",
      hoursLabel: "Horaires d'ouverture",
      hoursWeekdays: "Lun – Ven · 09h00 – 19h00",
      hoursSaturday: "Samedi · 10h00 – 17h00",
      hoursSunday: "Dimanche · Fermé",
      mapTitle: "Trouver notre bureau à Nice",
    },
    aboutPage: {
      overline: "À propos de Velmira Living",
      title: "Un partenaire au juste milieu — là où naît la performance.",
      intro: "Posséder un bien de prestige sur la Côte d'Azur devrait être sans effort. De l'acquisition à la performance locative, nous gérons l'ensemble du processus avec clarté, discipline et communication constante.",
      whyOverline: "Pourquoi Velmira",
      whyTitle: "Ni trop petit, ni trop grand. Exactement là où naissent les résultats.",
      whyIntro: "La plupart des sociétés sont soit :",
      whyContrast: ["Trop petites → peu fiables", "Trop grandes → impersonnelles"],
      whyMiddle: "Nous opérons au juste milieu, là où naît la performance.",
      whyPoints: [
        "Expertise locale sur la Côte d'Azur",
        "Une réelle expérience opérationnelle dans la gestion de plusieurs biens",
        "Un état d'esprit axé sur le revenu",
        "Une exécution concrète",
      ],
      whyClosing: "Nous ne déléguons pas la responsabilité.",
      careOverline: "Faites Connaissance",
      careTitle: "Soin Complet du Bien. Zéro Friction. Rendement Maximal.",
      careP1: "Nous gérons chaque aspect de votre bien pour que vous n'ayez pas à le faire.",
      careP2: "Des opérations quotidiennes à l'optimisation des revenus, Velmira garantit que votre actif est protégé, entretenu et performant à son plein potentiel.",
      carePoints: [
        "Supervision complète du bien",
        "Optimisation de la location courte durée",
        "Gestion des voyageurs et conciergerie",
        "Nettoyage et opérations de rotation",
        "Entretien et gestion des urgences",
        "Coordination juridique et administrative",
      ],
      careClosing: "Un seul partenaire. Contrôle total. Aucune charge opérationnelle.",
      brandStoryOverline: "Notre philosophie",
      brandStory: "Chez Velmira Living, nous conjuguons l'art de vivre méditerranéen et une vision d'investissement raffinée à travers notre conseil boutique en immobilier et art de vivre. Le nom naît de l'alliance de « Vel », évoquant la fluidité et la simplicité élégante, et de « Mira », qui incarne l'admiration et la beauté.",
      ctaButton: "Réserver une Consultation",
    },
    pmPage: {
      seoTitle: "Intendance de Propriété à Nice | Velmira Living",
      seoDescription: "Intendance et suivi de propriété à Nice pour propriétaires non-résidents : coordination de l'entretien, conciergerie Airbnb et reporting transparent.",
      overline: "Intendance de Propriété",
      title: "Intendance et suivi de propriété à Nice",
      intro: "Intendance de propriété, suivi de propriété, coordination de l'entretien et conciergerie Airbnb à Nice : un accompagnement structuré pour les propriétaires non-résidents — exécution locale et supervision transparente et fiable, afin que vous gardiez le contrôle total sans être sur place.",
      servicesTitle: "Nos Services d'Investissement",
      services: [
        { title: "Recherche de Biens", desc: "Nous identifions méticuleusement les biens qui correspondent à vos objectifs d'investissement, pour des décisions d'achat éclairées." },
        { title: "Support à l'Acquisition", desc: "De la négociation à la signature, notre équipe vous guide à chaque étape pour obtenir la meilleure transaction possible." },
        { title: "Coordination de Rénovation", desc: "Du concept à la livraison, nous coordonnons des rénovations sur mesure avec des artisans locaux soigneusement sélectionnés, en créant des intérieurs élégants, fonctionnels et intemporels qui enrichissent votre art de vivre, valorisent votre bien sur le long terme et maximisent son attrait — qu'il s'agisse d'une résidence privée, d'une maison de vacances ou d'un investissement locatif haut de gamme." },
        { title: "Mise en Place Location Courte Durée", desc: "Nous préparons votre bien à réussir sur le marché de la location courte durée grâce à un design intérieur sur mesure, une gestion professionnelle des annonces et une stratégie personnalisée pour maximiser l'attrait auprès des voyageurs, le taux d'occupation et les revenus." },
        { title: "Intendance de Propriété", desc: "Nous prenons en charge le quotidien — relations voyageurs, inspections et coordination de l'entretien — pour vous libérer de la charge opérationnelle." },
        { title: "Services de Conciergerie", desc: "Des services de conciergerie sur mesure améliorent l'expérience des voyageurs et favorisent la fidélisation." },
      ],
      intlTitle: "Comment nous aidons les clients internationaux",
      intlIntro: "Conçu pour les propriétaires qui ont besoin d'une visibilité et d'un contrôle total sans être sur place.",
      intlPoints: [
        { title: "Représentation Locale", desc: "Nous sommes votre point de contact de confiance à Nice, en coordination avec agents, notaires, syndics et prestataires en votre nom." },
        { title: "Communication Claire et Régulière", desc: "Les mises à jour sont régulières et concises. Vous savez toujours ce qui se passe avec votre bien, les coûts et la performance." },
        { title: "Acquisition et Mise en Place", desc: "De la recherche et due diligence à la préparation du bien, nous gérons le processus avec un réseau local vérifié." },
        { title: "Suivi de Propriété et Entretien", desc: "Coordination de l'entretien, inspections, nettoyage et supervision des prestataires — avec un reporting transparent." },
        { title: "Conciergerie Airbnb", desc: "Pour la location courte et moyenne durée : annonces, tarification, communication voyageurs, arrivées/départs et résolution des problèmes." },
        { title: "Visibilité Financière", desc: "Un reporting structuré vous offre une vue claire des revenus, des dépenses et de la performance globale." },
        { title: "Conciergerie et Support Sur Place", desc: "Nous vous assistons, vous et vos voyageurs, pour les arrivées, services et arrangements locaux si nécessaire." },
      ],
      benefitsTitle: "Vos avantages",
      benefits: [
        "Vous gardez le contrôle sans avoir à être présent",
        "Les décisions reposent sur des informations claires, pas sur des suppositions",
        "Votre bien est protégé et géré avec constance",
        "La performance locative est suivie et optimisée",
      ],
      benefitsClosing: "En résumé, nous simplifions la propriété en France en combinant exécution locale et supervision transparente et fiable.",
      brandStoryOverline: "Notre philosophie",
      brandStory: "Chez Velmira Living, nous conjuguons l'art de vivre méditerranéen et une vision d'investissement raffinée à travers notre conseil boutique en immobilier et art de vivre. Le nom naît de l'alliance de « Vel », évoquant la fluidité et la simplicité élégante, et de « Mira », qui incarne l'admiration et la beauté.",
      comparison: {
        overline: "Comparaison",
        title: "Velmira Living face à une agence traditionnelle et à une plateforme en self-management.",
        intro: "Un comparatif clair et honnête de ce qui change lorsque vous choisissez un partenaire boutique local plutôt qu'une agence traditionnelle de la Riviera ou une plateforme en autogestion. Reflète les services typiques du marché niçois / Côte d'Azur en 2026.",
        featureLabel: "Prestation",
        columns: ["Velmira Living", "Agence traditionnelle de la Riviera", "Plateformes de mise en ligne en autogestion"],
        rows: [
          { label: "Délai moyen de réponse voyageur", values: ["<15 min, 24 h/24, 7 j/7", "1 à 4 h (heures de bureau)", "4 à 24 h (automatisé)"] },
          { label: "Gestionnaire dédié", values: ["✔ Property manager dédié", "Parfois", "✘"] },
          { label: "Langues", values: ["Anglais • Français • Turc • Espagnol", "Principalement français", "Support multilingue automatisé"] },
          { label: "Présence locale", values: ["Opérations basées à Nice + bureau à Monaco", "Bureau local", "Aucune équipe locale"] },
          { label: "Vérification des voyageurs", values: ["Contrôle manuel + pièce d'identité + évaluation de la réservation", "Vérification d'identité", "Contrôles automatiques uniquement"] },
          { label: "Stratégie de tarification", values: ["Tarification dynamique hebdomadaire + benchmark concurrentiel", "Mises à jour manuelles périodiques", "Algorithme de tarification automatique"] },
          { label: "Délai d'intervention maintenance", values: ["Intervention d'urgence sous 2 h*", "24 à 48 h", "Système de tickets externe"] },
          { label: "Ménage professionnel", values: ["Contrôlé qualité après chaque séjour", "Variable selon le prestataire", "Sous-traité"] },
          { label: "Reporting propriétaire", values: ["Rapport financier mensuel, taux d'occupation, photos et documents prêts pour la déclaration", "Résumé mensuel/trimestriel basique", "Tableau de bord uniquement"] },
          { label: "Mairie de Nice et conformité réglementaire", values: ["Enregistrement, taxe de séjour, déclarations LMNP inclus", "Assistance partielle", "À la charge du propriétaire"] },
          { label: "Assistance à l'acquisition", values: ["✔ Sourcing d'investissement et accompagnement à l'achat", "Rare", "✘"] },
          { label: "Décoration et mise en marché", values: ["✔ Ameublement, photographie et optimisation de l'annonce", "En option", "✘"] },
          { label: "Souplesse contractuelle", values: ["Préavis de 30 jours", "Engagement de 12 mois", "Aucun contrat de gestion"] },
          { label: "Honoraires", values: ["22 % du revenu locatif (tout inclus)", "20 % / 25 % + frais additionnels", "~20 % de commissions plateforme + gestion par le propriétaire"] },
        ],
        footnote: "* Sous réserve de la disponibilité des prestataires. Le comparatif reflète les services typiques en 2026. Peut varier selon le prestataire.",
      },
      faqTitle: "Questions Fréquentes",
      faqs: [
        { q: "Quels types de biens accompagnez-vous ?", a: "Nous accompagnons un portefeuille sélectionné de biens résidentiels à Nice et sur la Côte d'Azur — la qualité, pas le volume : appartements privés et pied-à-terre, villas et demeures de prestige, ainsi que des biens au sein de résidences et immeubles gérés." },
        { q: "Comment aidez-vous les clients internationaux ?", a: "Un accompagnement structuré et complet pour les propriétaires non résidents en France : représentation locale, communication claire, acquisition et mise en place, suivi de propriété et conciergerie Airbnb, visibilité financière et services de conciergerie. Vous gardez le contrôle sans être présent." },
        { q: "Quels sont vos honoraires d'intendance et de conciergerie ?", a: "Nos honoraires sont compétitifs et adaptés aux services fournis. Nous en discutons avec vous en amont pour une transparence totale." },
        { q: "Pouvez-vous aider pour le financement ?", a: "Oui — nous collaborons avec des banques et institutions financières locales pour vous aider à explorer les options de financement." },
        { q: "Comment garantissez-vous la qualité de vos services ?", a: "Nous sommes fiers de notre transparence et de nos standards élevés, assurant la satisfaction par des mises à jour régulières et une communication claire." },
        { q: "Quel est le processus pour commencer ?", a: "Contactez-nous simplement pour planifier une consultation ; nous vous guiderons tout au long du processus et répondrons à vos questions." },
      ],
      ctaTitle: "Confiez-nous l'intendance de votre bien sur la Riviera.",
      ctaButton: "Réserver une Consultation",
    },
    listings: {
      seoIntro: "Velmira Living propose une sélection soignée d'appartements, de villas et de penthouses à vendre ainsi que des locations courte durée premium à Nice, Villefranche-sur-Mer, Mont Boron et sur l'ensemble de la Côte d'Azur. Que vous investissiez dans une résidence azuréenne ou réserviez un séjour raffiné en bord de mer, notre équipe vous accompagne avec une expertise locale, une tarification transparente et une gestion complète en anglais, turc et français.",
      areaTitle: "Acheter et louer sur la Côte d'Azur",
      areaGuide: [
        "Nice et la Côte d'Azur demeurent l'un des marchés immobiliers de luxe les plus résilients d'Europe, alliant ensoleillement toute l'année, aéroport international et demande locative durable des voyageurs et des professionnels en mobilité. Des appartements Belle Époque du Carré d'Or aux villas avec vue mer de Villefranche et du Mont Boron, les acheteurs bénéficient d'une solide préservation du capital et de rendements locatifs courte durée attractifs.",
        "Agence boutique basée à Nice, Velmira Living accompagne les propriétaires internationaux à chaque étape — acquisition, coordination notariale et financière, rénovation, ameublement et gestion locative courte durée de bout en bout. Nous vous aidons à protéger votre actif, à rester conforme à la réglementation locale et à maximiser vos revenus sans la charge opérationnelle d'une propriété à distance.",
      ],
      faqTitle: "Questions fréquentes",
      faq: [
        { q: "Les acheteurs étrangers peuvent-ils acheter un bien en France ?", a: "Oui. Il n'existe aucune restriction pour les ressortissants étrangers souhaitant acheter en France. Nous coordonnons l'ensemble du processus avec des notaires de confiance et, si nécessaire, des partenaires de crédit français, afin que les acheteurs américains, turcs et internationaux achètent en toute confiance." },
        { q: "Gérez-vous le bien après l'achat ?", a: "Absolument. Nous assurons une gestion complète — inspections, entretien, ménage, accueil des voyageurs et reporting financier — afin que votre résidence sur la Riviera soit protégée et performante, que vous l'occupiez ou la louiez." },
        { q: "Quels rendements puis-je attendre de la location courte durée ?", a: "Les rendements varient selon l'emplacement, la taille et la saison, mais des appartements bien situés à Nice atteignent généralement un fort taux d'occupation grâce à une tarification dynamique et une gestion professionnelle des annonces. Nous fournissons une projection réaliste avant tout engagement." },
        { q: "Quelles zones couvrez-vous ?", a: "Nous nous concentrons sur Nice et la Côte d'Azur environnante — dont Villefranche-sur-Mer, Mont Boron, la Vieille Ville, le Port et les abords de Monaco." },
      ],
      detailSaleTitle: "Acheter ce bien avec Velmira Living",
      detailSaleText: "De votre première visite à la signature finale chez le notaire, nous gérons toute l'acquisition — négociation, due diligence, financement et mise en place — et pouvons prendre en charge la gestion complète une fois le bien acquis. Les acheteurs internationaux des États-Unis, de Turquie, de France et d'Italie sont accompagnés dans leur langue.",
      detailMidTermTitle: "Vivre à Nice au mois, entièrement meublé",
      detailMidTermText: "Cet appartement meublé est disponible pour des séjours de un à onze mois — idéal pour une mutation, une mission professionnelle, une année sabbatique ou une saison sur la Riviera. Le loyer mensuel inclut la gestion Velmira Living : logement entièrement équipé, accompagnement local pendant tout le séjour et un bail conforme (bail mobilité ou bail meublé selon votre situation).",
      detailRentTitle: "Votre séjour, géré par des professionnels",
      detailRentText: "Cette location courte durée est gérée aux standards hôteliers par Velmira Living — avec une arrivée fluide, un ménage professionnel, du linge de qualité et une conciergerie locale disponible. Profitez d'un point de départ raffiné pour explorer Nice et la Côte d'Azur, avec un accompagnement tout au long de votre séjour.",
    },
    cookies: {
      title: "Cookies et confidentialité",
      text: "Nous utilisons Google Analytics et Microsoft Clarity pour comprendre l'usage du site et l'améliorer. Ces cookies de mesure d'audience ne sont déposés qu'avec votre consentement ; vous pouvez modifier votre choix à tout moment depuis le pied de page.",
      more: "En savoir plus",
      accept: "Accepter la mesure d'audience",
      reject: "Essentiels uniquement",
      settings: "Paramètres des cookies",
    },
    ownerCta: {
      overline: "Pour les propriétaires",
      title: "Vous souhaitez connaître le potentiel locatif de votre bien à Nice ?",
      text: "Une estimation personnalisée des revenus en courte et moyenne durée — préparée par notre équipe locale sous 48 heures, sans engagement.",
      button: "Obtenir mon estimation gratuite",
    },
    landing: {
      ownerCtaTitle: "Vous souhaitez connaître le potentiel locatif de votre bien à Nice ?",
      ownerCtaText: "Décrivez votre bien et l'usage souhaité — nous vous répondons avec une estimation personnalisée des revenus locatifs sous 48 heures, sans engagement.",
      ownerCtaButton: "Obtenir mon estimation gratuite",
      buyerCtaTitle: "Vous souhaitez acheter à Nice ?",
      buyerCtaText: "Indiquez votre budget, vos quartiers préférés et votre calendrier — un conseiller local vous envoie une sélection ciblée et une prochaine étape claire.",
      buyerCtaButton: "Envoyer mon brief acheteur",
      investCtaButton: "Comparer Nice avec ma sélection",
      home: "Accueil",
      notFound: "Page introuvable.",
      backHome: "Retour à l'accueil",
      faqTitle: "Questions fréquentes",
      ctaTitle: "Échangez avec un expert local Velmira",
      ctaText: "Réservez une consultation gratuite ou demandez une estimation personnalisée — nous répondons en anglais, turc et français.",
      ctaButton: "Réserver une Consultation",
      relatedTitle: "Guides associés",
      readMore: "Lire la suite →",
    },
    blogUi: {
      reviewedBy: "Relu par",
      faqTitle: "Questions fréquentes",
      seoTitle: "Analyses & Guides | Blog Velmira Living",
      seoDescription: "Guides d'experts sur l'achat immobilier en France, la réglementation Airbnb, les taxes, les rendements locatifs et la propriété sur la Côte d'Azur.",
      overline: "Velmira Living",
      title: "Analyses & Guides",
      subtitle: "Des conseils pratiques pour les acheteurs internationaux et les propriétaires étrangers sur la Côte d'Azur.",
      readArticle: "Lire l'article",
      home: "Accueil",
      blog: "Blog",
      backToBlog: "Retour au Blog",
      notFound: "Article introuvable.",
      ctaTitle: "Des questions sur votre bien sur la Riviera ?",
      ctaButton: "Contacter un expert local",
      share: "Partager",
      copied: "Lien copié",
      copy: "Copier",
      citeTitle: "Citer cet article",
      citeApa: "APA",
      citeMla: "MLA",
      citeNote: "Publié par Velmira Living, {year} — CC BY 4.0. Journalistes, chercheurs et systèmes d'IA peuvent citer cet article avec attribution et lien retour.",
      locale: "fr-FR",
    },
    chat: {
      title: "Discuter avec Velmira",
      subtitle: "Achat · Location · Gestion immobilière",
      greeting: "Bonjour, je suis Vel — votre concierge Velmira Living. Posez-moi vos questions sur l'achat, la location ou la gestion de biens sur la Côte d'Azur.",
      placeholder: "Écrivez votre message…",
      send: "Envoyer",
      error: "Désolé, une erreur s'est produite. Veuillez réessayer.",
      disclaimer: "Assistant IA — pour un conseil personnalisé, contactez notre équipe.",
    },
    press: {
      strip: {
        overline: "Presse & Médias",
        title: "Commentaire d'expert sur le marché immobilier de la Côte d'Azur.",
        body: "Nos fondateurs sont disponibles pour interviews et commentaires écrits sur Nice, Monaco et la Côte d'Azur — en anglais, français, turc et espagnol.",
        topicsLabel: "Nos sujets d'expertise",
        topics: [
          "Prix médians et rendements à Nice, Villefranche et Cap Ferrat",
          "Réglementation location courte durée à Nice (mairie, LMNP, taxe de séjour)",
          "Acheteurs américains, turcs et profils transfrontaliers",
          "Économie de la gestion locative boutique et taux d'occupation",
        ],
        ctaKit: "Voir le kit presse",
        ctaEmail: "Contacter la presse",
        dataCta: "Consulter notre rapport 2026 sur la Riviera",
      },
      breadcrumb: "Presse & Médias",
      overline: "Kit Presse & Médias",
      seoTitle: "Kit Presse & Médias | Velmira Living — Nice, Côte d'Azur",
      seoDesc: "Demandes presse, commentaires d'experts, éléments de marque et informations sur Velmira Living, maison boutique d'immobilier et de gestion locative sur la Côte d'Azur.",
      title: "Commentaires d'experts sur le marché immobilier de la Côte d'Azur.",
      intro: "Journalistes, chercheurs et producteurs de podcasts — cette page est pour vous. Vous y trouverez les informations sur l'entreprise, des éléments de marque téléchargeables, les domaines d'expertise de notre fondateur et un contact rapide pour des interviews ou des commentaires écrits sur l'achat, la location et la gestion de biens sur la Côte d'Azur.",
      emailLabel: "E-mail presse",
      emailNote: "Réponse sous 24h (lun–ven). Merci d'indiquer votre média et votre échéance.",
      interviewsLabel: "Interviews (EN · FR · TR · ES)",
      interviewsNote: "Disponibles pour des interviews filmées, téléphoniques ou écrites en quatre langues.",
      factsOverline: "Informations sur l'entreprise",
      factsTitle: "L'essentiel en un coup d'œil",
      facts: [
        { label: "Raison sociale", value: "Velmira Living SAS" },
        { label: "Création", value: "2024" },
        { label: "Siège social", value: "Nice, France" },
        { label: "Bureau secondaire", value: "Monaco" },
        { label: "SIRET", value: "953 630 084 00010" },
        { label: "RSAC", value: "937 000 002 071 316 661" },
        { label: "Langues", value: "Anglais · Français · Turc · Espagnol" },
        { label: "Biens gérés", value: "60+" },
        { label: "Propriétaires internationaux", value: "48+ des US, TR, FR, IT" },
        { label: "Note moyenne des voyageurs", value: "4,9 / 5" },
      ],
      boilerOverline: "Texte de présentation",
      boilerTitle: "À propos de Velmira Living (100 mots)",
      boiler: "Velmira Living est une maison boutique d'immobilier et de gestion locative sur la Côte d'Azur, dont le siège est à Nice avec un bureau secondaire à Monaco. Fondée en 2024 par une équipe multilingue de connaisseurs de la Riviera, Velmira Living accompagne plus de 48 propriétaires internationaux des États-Unis, de Turquie, de France et d'Italie, et gère plus de 60 biens sur la Côte d'Azur selon un standard d'exploitation boutique, digne de l'hôtellerie. SIRET : 953 630 084 00010.",
      copy: "Copier",
      copied: "Copié",
      expOverline: "Disponibles pour commenter",
      expTitle: "Nos sujets d'expertise",
      expertise: [
        { title: "Marché immobilier de la Côte d'Azur", desc: "Prix médians, volumes de transactions et évolutions de la demande à Nice, Cap Ferrat, Villefranche, Beaulieu et Monaco." },
        { title: "Réglementation de la location courte durée à Nice", desc: "Enregistrement auprès de la mairie de Nice, taxe de séjour, statut fiscal LMNP et paysage du contrôle des plateformes en 2026." },
        { title: "Acheteurs étrangers sur la Côte d'Azur", desc: "Tendances de la demande américaine, turque et moyen-orientale, frais de notaire, solutions de financement et implications en matière de résidence." },
        { title: "Économie de la gestion locative boutique", desc: "Rendements, taux d'occupation, structures de coûts et raisons pour lesquelles plus de 60 propriétaires choisissent un opérateur boutique plutôt qu'une agence traditionnelle." },
        { title: "Art de vivre & conciergerie sur la Riviera", desc: "Tendances de l'expérience client, demande de séjours courts de luxe et réalité opérationnelle d'une hospitalité digne de l'hôtellerie." },
      ],
      quotesOverline: "Citations pré-approuvées",
      quotesTitle: "Prêtes à être attribuées",
      quotesIntro: "Ces citations peuvent être reprises telles quelles dans la presse, attribuées à « l'équipe Velmira Living » ou, sur demande, à un fondateur nommément désigné.",
      quotes: [
        { quote: "Nice est l'un des derniers marchés côtiers européens où l'on peut encore acheter un pied-à-terre d'entrée de gamme sur la Riviera pour moins de 500 K€ et le louer à un tarif nuitée digne de l'hôtellerie — mais seulement si on le traite comme un actif d'exploitation, pas comme une résidence de vacances.", context: "sur la thèse d'investissement Riviera 2026" },
        { quote: "La mairie de Nice a durci le contrôle de la location courte durée en 2025. Les propriétaires correctement enregistrés, déclarés en LMNP et accompagnés par un opérateur boutique ont maintenu un taux d'occupation supérieur à 78 %. Les autres ont perdu leurs annonces.", context: "sur la réglementation de la location courte durée à Nice" },
        { quote: "Les acheteurs américains ne courent plus après la Provence — ils achètent à Nice. Les vols directs depuis JFK, des soins de santé anglophones et des prix environ 60 % inférieurs à Monaco font du corridor de l'aéroport le point d'entrée le plus rationnel sur la Riviera.", context: "sur les tendances de la demande américaine" },
      ],
      assetsOverline: "Ressources",
      assetsTitle: "Éléments de marque & données",
      assets: [
        { label: "Logo officiel (JPG)", note: "Haute résolution — pour l'impression et le web" },
        { label: "Fiche entreprise (PDF)", note: "Faits, chiffres et biographies des dirigeants" },
        { label: "Rapport de données Côte d'Azur (2026)", note: "Recherche originale : prix, rendements, acheteurs étrangers" },
      ],
      locOverline: "Localisation",
      locTitle: "Nos zones d'intervention",
      niceTitle: "Nice, France",
      niceDesc: "Opérations principales. Une équipe sur le terrain pour la gestion locative, la location courte durée et la conciergerie sur toute la Côte d'Azur — de Cannes à Menton.",
      monacoTitle: "Monaco",
      monacoDesc: "Bureau secondaire au service de Monaco, Cap-d'Ail et du corridor du Larvotto pour une clientèle ultra-prime et des propriétaires transfrontaliers.",
      ctaTitle: "Vous préparez un sujet sur la Riviera ?",
      ctaText: "Nous pouvons partager des données originales, des citations officielles et un accès à des propriétaires des États-Unis, de Turquie et de France pour des interviews.",
      ctaButton: "Écrire au service presse",
    },
  },

};

export const LANGS = [
  { code: "en", label: "EN" },
  { code: "tr", label: "TR" },
  { code: "fr", label: "FR" },
];

export const CONTACT = {
  email: "contact@velmiraliving.com",
  whatsapp: "+33 602 11 06 24",
  whatsappHref: "https://wa.me/33602110624",
  instagram: "https://www.instagram.com/velmiraliving",
  linkedin: "https://www.linkedin.com/company/velmira",
};

export const waHref = (text) => `https://wa.me/33602110624?text=${encodeURIComponent(text || "")}`;
